import { useState, useMemo } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { useClient } from '@/contexts/ClientContext';
import { useMarketOverview, useMarketSegments, usePortfolioPosition, useMarketSeasonality, useMarketCosts } from '@/hooks/useMarket';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/api/client';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { SidebarLayout, SidebarItem } from '@/components/layout/SidebarLayout';
import { InsightCard } from '@/components/story/StorySection';
import { MarketTrendChart, DonutChart } from '@/components/ui/Charts';
import { SectionHeaderWithFreshness, PROVENANCE_PRESETS } from '@/components/ui/DataFreshness';
import { ProfileBuilder, DerivedPersonaCard, ComparisonSummary, SegmentsTable, CrossTabsView } from '@/components/consumers';
import { ChannelMap } from '@/components/channel/ChannelMap';
import { useProfileGeneration } from '@/hooks/useProfileGeneration';
import type { ProfileSelections, DerivedPersona, Segment } from '@/types/consumer';

// ============================================================================
// 5C FRAMEWORK: Category, Consumer, Channel, Competition, Company
// Standard strategic analysis framework for CPG/FMCG
// ============================================================================

const FIVE_C = {
  category: { en: 'Category', es: 'Categoría', abbrev: 'CAT', icon: '📊' },
  consumer: { en: 'Consumer', es: 'Consumidor', abbrev: 'CON', icon: '👥' },
  channel: { en: 'Channel', es: 'Canal', abbrev: 'CHN', icon: '🏪' },
  competition: { en: 'Competition', es: 'Competencia', abbrev: 'CMP', icon: '⚔️' },
  company: { en: 'Company', es: 'Empresa', abbrev: 'CO', icon: '🏢' },
};

// Format currency with comma thousands separator
// LatAm: Use "Miles de MM" for 10^9 (Spanish billón = 10^12, NOT 10^9)
function formatCurrency(value: number, currency: string = 'USD', language: string = 'en'): string {
  const t = language === 'es';
  const fmt = (n: number, decimals: number = 1) =>
    n.toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });

  if (value >= 1e9) {
    // 10^9: English "B", Spanish "Miles de MM"
    const num = fmt(value / 1e9);
    return t ? `${num} Miles de MM ${currency}` : `${num}B ${currency}`;
  }
  if (value >= 1e6) {
    // 10^6: "M" or "MM" (millions)
    const num = fmt(value / 1e6);
    return t ? `${num} MM ${currency}` : `${num}M ${currency}`;
  }
  if (value >= 1e3) {
    const num = fmt(value / 1e3, 0);
    return `${num}K ${currency}`;
  }
  return `${value.toLocaleString('en-US')} ${currency}`;
}

// Format volume with appropriate suffix (language-aware)
function formatVolume(volume: number, language: string = 'en'): string {
  const t = language === 'es';
  const fmt = (n: number, decimals: number = 1) =>
    n.toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });

  if (volume >= 1e9) {
    const num = fmt(volume / 1e9);
    return t ? `${num} Miles de MM` : `${num}B`;
  }
  if (volume >= 1e6) {
    const num = fmt(volume / 1e6);
    return t ? `${num} MM` : `${num}M`;
  }
  if (volume >= 1e3) {
    return `${fmt(volume / 1e3, 0)}K`;
  }
  return volume.toLocaleString('en-US');
}

// Build sidebar items from 5C framework + Data Sources
function buildSidebarItems(language: string): SidebarItem[] {
  const t = language === 'es';
  return [
    {
      id: 'category',
      icon: <span className="text-lg">{FIVE_C.category.icon}</span>,
      name: t ? FIVE_C.category.es : FIVE_C.category.en,
      description: t ? 'Tamaño y crecimiento del mercado' : 'Market size and growth',
      subItems: [
        { id: 'size', label: t ? 'Tamaño del Mercado' : 'Market Size' },
        { id: 'structure', label: t ? 'Estructura' : 'Structure' },
        { id: 'trends', label: t ? 'Tendencias' : 'Trends' },
        { id: 'seasonality', label: t ? 'Estacionalidad' : 'Seasonality' },
      ],
    },
    {
      id: 'consumer',
      icon: <span className="text-lg">{FIVE_C.consumer.icon}</span>,
      name: t ? FIVE_C.consumer.es : FIVE_C.consumer.en,
      description: t ? 'Segmentos y comportamientos' : 'Segments and behaviors',
      subItems: [
        { id: 'segments', label: t ? 'Segmentos' : 'Segments' },
        { id: 'personas', label: 'Personas' },
        { id: 'compare', label: t ? 'Comparar' : 'Compare' },
        { id: 'crosstabs', label: 'Cross-Tabs' },
      ],
    },
    {
      id: 'channel',
      icon: <span className="text-lg">{FIVE_C.channel.icon}</span>,
      name: t ? FIVE_C.channel.es : FIVE_C.channel.en,
      description: t ? 'Dónde compran' : 'Where they buy',
      subItems: [
        { id: 'sell-flow', label: 'Sell-In / Sell-Out' },
        { id: 'distribution', label: t ? 'Distribución' : 'Distribution' },
        { id: 'retail', label: 'Retail' },
      ],
    },
    {
      id: 'competition',
      icon: <span className="text-lg">{FIVE_C.competition.icon}</span>,
      name: t ? FIVE_C.competition.es : FIVE_C.competition.en,
      description: t ? 'Jugadores y estrategias' : 'Players & strategies',
    },
    {
      id: 'company',
      icon: <span className="text-lg">{FIVE_C.company.icon}</span>,
      name: t ? FIVE_C.company.es : FIVE_C.company.en,
      description: t ? 'Tu posición' : 'Your position',
      subItems: [
        { id: 'position', label: t ? 'Posición' : 'Position' },
        { id: 'portfolio', label: 'Portfolio' },
        { id: 'costs', label: t ? 'Costos' : 'Costs' },
      ],
    },
    {
      id: 'data',
      icon: <span className="text-lg">📂</span>,
      name: t ? 'Fuentes de Datos' : 'Data Sources',
      description: t ? 'Calidad y recencia de datos' : 'Data quality and recency',
    },
  ];
}

export function UnderstandPage() {
  const { language } = useLanguage();
  const { selectedClient } = useClient();
  const [activeSection, setActiveSection] = useState('category');
  const [activeSubSection, setActiveSubSection] = useState('size');

  const sidebarItems = buildSidebarItems(language);

  const handleSectionChange = (sectionId: string) => {
    setActiveSection(sectionId);
    // Set default sub-section for new section
    const section = sidebarItems.find(s => s.id === sectionId);
    if (section?.subItems?.[0]) {
      setActiveSubSection(section.subItems[0].id);
    }
  };

  if (!selectedClient) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh]">
        <div className="text-4xl mb-4">🔍</div>
        <h2 className="text-xl font-semibold text-white mb-2">Select a Client</h2>
        <p className="text-text-secondary text-center max-w-md">
          Choose a client from the dropdown above to explore their market landscape.
        </p>
      </div>
    );
  }

  const clientId = selectedClient.client_id;
  const t = language === 'es';

  const activeItem = sidebarItems.find(s => s.id === activeSection);
  const contentHeader = activeItem && (
    <div className="flex items-center gap-3 mb-6">
      {activeItem.icon}
      <div>
        <h2 className="text-xl font-semibold text-white">{activeItem.name}</h2>
        <p className="text-sm text-text-secondary">{activeItem.description}</p>
      </div>
    </div>
  );

  return (
    <SidebarLayout
      title={t ? 'Entender el Mercado' : 'Understand the Market'}
      subtitle={t ? 'Análisis 5C' : '5C Analysis'}
      items={sidebarItems}
      activeItemId={activeSection}
      activeSubItemId={activeSubSection}
      onItemChange={handleSectionChange}
      onSubItemChange={setActiveSubSection}
      contentHeader={contentHeader}
    >
      <UnderstandContent
        section={activeSection}
        subSection={activeSubSection}
        clientId={clientId}
        language={language}
      />
    </SidebarLayout>
  );
}

// Main content router
function UnderstandContent({
  section,
  subSection,
  clientId,
  language,
}: {
  section: string;
  subSection: string;
  clientId: string;
  language: string;
}) {
  switch (section) {
    case 'category':
      return <CategorySection clientId={clientId} language={language} subSection={subSection} />;
    case 'consumer':
      return <ConsumerSection clientId={clientId} language={language} subSection={subSection} />;
    case 'channel':
      return <ChannelSection clientId={clientId} language={language} subSection={subSection} />;
    case 'competition':
      return <CompetitionSection clientId={clientId} language={language} />;
    case 'company':
      return <CompanySection clientId={clientId} language={language} subSection={subSection} />;
    case 'data':
      return <DataSourcesSection clientId={clientId} language={language} />;
    default:
      return null;
  }
}

// ============================================================================
// CATEGORY SECTION
// ============================================================================
function CategorySection({ clientId, language, subSection }: { clientId: string; language: string; subSection: string }) {
  const { data, isLoading } = useMarketOverview(clientId);
  const t = language === 'es';

  if (isLoading) return <LoadingSpinner size="md" />;
  if (!data) return null;

  // Render sub-sections
  if (subSection === 'trends') {
    return <CategoryTrendsView data={data} language={language} />;
  }
  if (subSection === 'structure') {
    return <CategoryStructureView language={language} />;
  }
  if (subSection === 'seasonality') {
    return <SeasonalityView clientId={clientId} language={language} />;
  }

  // Default: Market Size view
  const value = data.market?.total_value || 0;
  const volume = data.market?.total_volume || 0;
  const growth = (data.market?.growth_rate || 0) * 100;
  const currency = data.market?.currency || 'USD';

  return (
    <div className="space-y-6">
      {/* KPI Cards */}
      <div className="grid grid-cols-3 gap-4">
        <div className="card p-6 text-center">
          <div className="text-xs text-text-tertiary uppercase tracking-wider mb-2">
            {t ? 'Valor Total' : 'Total Value'}
          </div>
          <div className="text-3xl font-bold text-white">{formatCurrency(value, currency, language)}</div>
        </div>
        <div className="card p-6 text-center">
          <div className="text-xs text-text-tertiary uppercase tracking-wider mb-2">
            {t ? 'Volumen Total' : 'Total Volume'}
          </div>
          <div className="text-3xl font-bold text-white">
            {formatVolume(volume, language)} {t ? 'unidades' : 'units'}
          </div>
        </div>
        <div className="card p-6 text-center">
          <div className="text-xs text-text-tertiary uppercase tracking-wider mb-2">
            {t ? 'Crecimiento YoY' : 'YoY Growth'}
          </div>
          <div className={`text-3xl font-bold ${growth >= 0 ? 'text-success-400' : 'text-danger-400'}`}>
            {growth >= 0 ? '+' : ''}{growth.toFixed(1)}%
          </div>
        </div>
      </div>

      {/* Insights */}
      <InsightCard
        text={growth > 3
          ? (t ? `El mercado está creciendo ${growth.toFixed(1)}% anual. Buen momento para invertir en participación.` : `The market is growing at ${growth.toFixed(1)}% annually. Good time to invest in share.`)
          : (t ? `El mercado crece modestamente ${growth.toFixed(1)}%. Enfócate en eficiencia y márgenes.` : `The market is growing modestly at ${growth.toFixed(1)}%. Focus on efficiency and margins.`)}
        type={growth > 3 ? 'success' : 'info'}
        sources={[
          { name: 'Nielsen Retail Audit', type: 'nielsen', date: '2025 Q4', confidence: 0.95 }
        ]}
        language={language}
      />

      {/* Growth Outlook */}
      {data.growth_outlook && (
        <div className="card p-6 bg-primary-500/5 border-primary-500/20">
          <div className="flex items-start gap-3">
            <span className="text-xl">💡</span>
            <div>
              <h3 className="text-sm font-medium text-primary-400 mb-1">
                {t ? 'Perspectiva de Crecimiento' : 'Growth Outlook'}
              </h3>
              <p className="text-text-secondary text-sm">{data.growth_outlook}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Category Structure View - Market breakdown by tier, format, occasion
function CategoryStructureView({ language }: { language: string }) {
  const t = language === 'es';

  // Market structure data
  const priceTierData = [
    { name: t ? 'Premium' : 'Premium', value: 35, color: '#38bdf8' },
    { name: t ? 'Medio' : 'Mid-Tier', value: 45, color: '#fbbf24' },
    { name: t ? 'Económico' : 'Economy', value: 20, color: '#6ee7b7' },
  ];

  const formatData = [
    { name: t ? 'Individual' : 'Single-Serve', value: 40, color: '#38bdf8' },
    { name: t ? 'Familiar' : 'Family Pack', value: 35, color: '#fbbf24' },
    { name: t ? 'Multipack' : 'Multipack', value: 15, color: '#fb7185' },
    { name: t ? 'A Granel' : 'Bulk', value: 10, color: '#a5b4fc' },
  ];

  const occasionData = [
    { name: t ? 'Desayuno' : 'Breakfast', value: 30, color: '#38bdf8' },
    { name: t ? 'Snack' : 'Snacking', value: 25, color: '#fbbf24' },
    { name: t ? 'Comida' : 'Meal', value: 20, color: '#6ee7b7' },
    { name: t ? 'Indulgencia' : 'Indulgence', value: 15, color: '#fb7185' },
    { name: t ? 'Otro' : 'Other', value: 10, color: '#a5b4fc' },
  ];

  // Breakdown section component
  const BreakdownCard = ({
    title,
    data,
  }: {
    title: string;
    data: { name: string; value: number; color: string }[];
  }) => (
    <div className="card p-5">
      <h3 className="text-sm font-medium text-text-secondary uppercase tracking-wider mb-4">{title}</h3>
      <div className="grid grid-cols-2 gap-4 items-center">
        {/* Donut Chart */}
        <div className="flex justify-center">
          <DonutChart
            data={data}
            height={140}
            showLegend={false}
          />
        </div>
        {/* Legend with bars */}
        <div className="space-y-2">
          {data.map((item) => (
            <div key={item.name}>
              <div className="flex items-center gap-2 mb-1">
                <div
                  className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                  style={{ backgroundColor: item.color }}
                />
                <span className="text-xs text-white flex-1 truncate">{item.name}</span>
                <span className="text-xs font-semibold text-white">{item.value}%</span>
              </div>
              <div className="h-1 bg-surface-600 rounded-full overflow-hidden ml-4">
                <div
                  className="h-full rounded-full"
                  style={{ width: `${item.value}%`, backgroundColor: item.color }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-4">
      <BreakdownCard title={t ? 'Por Nivel de Precio' : 'By Price Tier'} data={priceTierData} />
      <BreakdownCard title={t ? 'Por Formato' : 'By Format'} data={formatData} />
      <BreakdownCard title={t ? 'Por Ocasión' : 'By Occasion'} data={occasionData} />

      {/* Insight */}
      <div className="card p-4 bg-primary-500/5 border-primary-500/20">
        <div className="flex items-start gap-3">
          <span className="text-lg">💡</span>
          <div>
            <p className="text-sm font-medium text-white mb-1">{t ? 'Insight Clave' : 'Key Insight'}</p>
            <p className="text-sm text-text-secondary">
              {t
                ? 'El segmento Mid-Tier domina con 45% del mercado. Oportunidad de crecimiento en Premium (+2.3pp YoY).'
                : 'Mid-Tier segment dominates at 45% of market. Growth opportunity in Premium (+2.3pp YoY).'}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

// Category Trends View - Historical and projected trends
function CategoryTrendsView({ data, language }: { data: any; language: string }) {
  const t = language === 'es';
  const growth = (data.market?.growth_rate || 0) * 100;

  // Mock historical data for trend visualization
  const trendData = [
    { name: '2023 Q1', value: 480, volume: 115 },
    { name: '2023 Q2', value: 485, volume: 118 },
    { name: '2023 Q3', value: 492, volume: 120 },
    { name: '2023 Q4', value: 498, volume: 122 },
    { name: '2024 Q1', value: 505, volume: 123 },
    { name: '2024 Q2', value: 512, volume: 125 },
    { name: '2024 Q3', value: 518, volume: 126 },
    { name: '2024 Q4', value: 525, volume: 128 },
    { name: '2025 Q1', value: 532, volume: 129 },
    { name: '2025 Q2', value: 540, volume: 130 },
  ];

  const growthDrivers = [
    { label: t ? 'Premiumización' : 'Premiumization', change: '+2.3pp', positive: true },
    { label: t ? 'Consumidores Health-Conscious' : 'Health-Conscious Consumers', change: '+1.8pp', positive: true },
    { label: t ? 'Expansión E-commerce' : 'E-commerce Expansion', change: '+1.5pp', positive: true },
    { label: t ? 'Canal Tradicional' : 'Traditional Channel', change: '-0.8pp', positive: false },
  ];

  // Transform data for MarketTrendChart
  const chartData = trendData.map(d => ({ period: d.name, value: d.value }));
  const startValue = trendData[0]?.value || 0;
  const currentValue = trendData[trendData.length - 1]?.value || 0;
  const changePercent = startValue > 0 ? ((currentValue - startValue) / startValue) * 100 : 0;

  return (
    <div className="space-y-6">
      {/* Trend Chart - Line Chart */}
      <div className="card p-6">
        <MarketTrendChart
          data={chartData}
          title={t ? 'Tendencia del Mercado (Últimos 3 Años)' : 'Market Trend (Last 3 Years)'}
          height={240}
          valueLabel={t ? 'Valor' : 'Value'}
          valueSuffix="M"
          showArea={true}
          highlightLast={true}
        />
        <div className="flex justify-between mt-6 pt-4 border-t border-surface-600">
          <div className="text-center">
            <div className="text-xs text-text-tertiary">{t ? 'Inicio' : 'Start'}</div>
            <div className="text-lg font-bold text-white">${startValue}M</div>
          </div>
          <div className="text-center">
            <div className="text-xs text-text-tertiary">{t ? 'Actual' : 'Current'}</div>
            <div className="text-lg font-bold text-primary-400">${currentValue}M</div>
          </div>
          <div className="text-center">
            <div className="text-xs text-text-tertiary">{t ? 'Cambio' : 'Change'}</div>
            <div className={`text-lg font-bold ${changePercent >= 0 ? 'text-success-400' : 'text-danger-400'}`}>
              {changePercent >= 0 ? '+' : ''}{changePercent.toFixed(1)}%
            </div>
          </div>
        </div>
      </div>

      {/* Growth Drivers */}
      <div className="card p-6">
        <h3 className="text-sm font-medium text-text-secondary uppercase tracking-wider mb-4">
          {t ? 'Drivers de Crecimiento' : 'Growth Drivers'}
        </h3>
        <div className="space-y-3">
          {growthDrivers.map((driver, idx) => (
            <div key={idx} className="flex items-center justify-between p-3 bg-surface-700/50 rounded-lg">
              <div className="flex items-center gap-3">
                <span className={`text-lg ${driver.positive ? 'text-success-400' : 'text-danger-400'}`}>
                  {driver.positive ? '↑' : '↓'}
                </span>
                <span className="text-sm text-white">{driver.label}</span>
              </div>
              <span className={`text-sm font-medium ${driver.positive ? 'text-success-400' : 'text-danger-400'}`}>
                {driver.change}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Projection Card */}
      <div className="card p-6 bg-primary-500/5 border-primary-500/20">
        <div className="flex items-start gap-3">
          <span className="text-xl">📈</span>
          <div>
            <h3 className="text-sm font-medium text-primary-400 mb-1">
              {t ? 'Proyección 2026' : '2026 Projection'}
            </h3>
            <p className="text-text-secondary text-sm">
              {t
                ? 'El mercado proyecta crecer 4.2% en valor y 2.8% en volumen. La premiumización seguirá siendo el principal driver.'
                : 'Market projected to grow 4.2% in value and 2.8% in volume. Premiumization remains the main driver.'}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// CONSUMER SECTION - With Named Personas
// ============================================================================

// Named personas for each segment type
const CONSUMER_PERSONAS: Record<string, { name: string; age: number; occupation: string; avatar: string; quote: string }[]> = {
  premium: [
    { name: 'María García', age: 38, occupation: 'Marketing Director', avatar: '👩‍💼', quote: 'I pay more for quality ingredients and trusted brands.' },
    { name: 'Carlos Mendoza', age: 45, occupation: 'Business Owner', avatar: '👨‍💼', quote: 'Time is money - I want premium products that deliver.' },
  ],
  family: [
    { name: 'Lucía Vargas', age: 42, occupation: 'School Administrator', avatar: '👩‍👦‍👦', quote: 'Weekend treats bring the whole family together.' },
    { name: 'Fernando Ruiz', age: 44, occupation: 'Accountant', avatar: '👨‍👩‍👧', quote: 'I look for products everyone can enjoy - no complaints at the table.' },
  ],
  kids: [
    { name: 'Patricia Moreno', age: 35, occupation: 'Pediatric Nurse', avatar: '👩‍⚕️', quote: 'My kids pick these out themselves. They love the fun packaging.' },
    { name: 'Andrés Castillo', age: 38, occupation: 'Elementary Teacher', avatar: '👨‍🏫', quote: 'Portion control matters - I buy kid-sized treats.' },
  ],
  value: [
    { name: 'Ana Rodríguez', age: 32, occupation: 'Teacher', avatar: '👩‍🏫', quote: 'I need to stretch my budget while feeding my family well.' },
    { name: 'Luis Hernández', age: 28, occupation: 'Delivery Driver', avatar: '🚚', quote: 'Good deals matter. I compare prices every week.' },
  ],
  health: [
    { name: 'Sofia Torres', age: 29, occupation: 'Fitness Instructor', avatar: '🧘‍♀️', quote: 'I read every label. No artificial ingredients for me.' },
    { name: 'Diego Ramírez', age: 35, occupation: 'Software Engineer', avatar: '👨‍💻', quote: 'I track macros and only buy clean products.' },
  ],
  convenience: [
    { name: 'Paula Sánchez', age: 41, occupation: 'Working Mother', avatar: '👩‍💻', quote: 'Quick and easy wins. I have 10 minutes for dinner prep.' },
    { name: 'Roberto Díaz', age: 26, occupation: 'Graduate Student', avatar: '🧑‍🎓', quote: 'Grab and go is my lifestyle. Ready-to-eat everything.' },
  ],
  indulgence: [
    { name: 'Valentina Reyes', age: 31, occupation: 'Graphic Designer', avatar: '👩‍🎨', quote: 'Life is short. I deserve a little treat after a long day.' },
    { name: 'Miguel Ángel', age: 27, occupation: 'Chef', avatar: '👨‍🍳', quote: 'I appreciate quality indulgences - flavor over quantity.' },
  ],
  traditional: [
    { name: 'Carmen Flores', age: 58, occupation: 'Homemaker', avatar: '👵', quote: 'I buy what my mother bought. Tradition matters.' },
    { name: 'José Martinez', age: 62, occupation: 'Retired Teacher', avatar: '👴', quote: 'I know what I like and I stick with it.' },
  ],
};

function getPersonasForSegment(segmentName: string): typeof CONSUMER_PERSONAS['premium'] {
  const name = segmentName.toLowerCase();
  // More specific matches first
  if (name.includes('family') || name.includes('familia')) return CONSUMER_PERSONAS.family;
  if (name.includes('kid') || name.includes('niño') || name.includes('children')) return CONSUMER_PERSONAS.kids;
  if (name.includes('indulgen') || name.includes('treat')) return CONSUMER_PERSONAS.indulgence;
  if (name.includes('premium') || name.includes('gourmet') || name.includes('luxury')) return CONSUMER_PERSONAS.premium;
  if (name.includes('value') || name.includes('price') || name.includes('budget') || name.includes('económic')) return CONSUMER_PERSONAS.value;
  if (name.includes('health') || name.includes('organic') || name.includes('natural') || name.includes('salud')) return CONSUMER_PERSONAS.health;
  if (name.includes('convenience') || name.includes('busy') || name.includes('quick') || name.includes('rápid')) return CONSUMER_PERSONAS.convenience;
  return CONSUMER_PERSONAS.traditional;
}

// Attribute/Level preferences for each segment type (CPG conjoint-style)
const SEGMENT_ATTRIBUTE_PREFERENCES: Record<string, Array<{
  attribute: string;
  attribute_es: string;
  levels: Array<{ level: string; level_es: string; preference: number }>;
}>> = {
  premium: [
    { attribute: 'Quality', attribute_es: 'Calidad', levels: [
      { level: 'Premium', level_es: 'Premium', preference: 0.65 },
      { level: 'Standard', level_es: 'Estándar', preference: 0.25 },
      { level: 'Basic', level_es: 'Básico', preference: 0.10 },
    ]},
    { attribute: 'Pack Size', attribute_es: 'Tamaño', levels: [
      { level: 'Single Serve', level_es: 'Individual', preference: 0.40 },
      { level: 'Multi-pack', level_es: 'Multi-pack', preference: 0.35 },
      { level: 'Family Size', level_es: 'Familiar', preference: 0.25 },
    ]},
    { attribute: 'Brand Origin', attribute_es: 'Origen', levels: [
      { level: 'Imported', level_es: 'Importado', preference: 0.55 },
      { level: 'Local Premium', level_es: 'Local Premium', preference: 0.35 },
      { level: 'Local', level_es: 'Local', preference: 0.10 },
    ]},
  ],
  family: [
    { attribute: 'Pack Size', attribute_es: 'Tamaño', levels: [
      { level: 'Family Size', level_es: 'Familiar', preference: 0.55 },
      { level: 'Multi-pack', level_es: 'Multi-pack', preference: 0.30 },
      { level: 'Single Serve', level_es: 'Individual', preference: 0.15 },
    ]},
    { attribute: 'Flavor Variety', attribute_es: 'Variedad', levels: [
      { level: 'Assorted', level_es: 'Surtido', preference: 0.50 },
      { level: 'Classic', level_es: 'Clásico', preference: 0.35 },
      { level: 'New Flavors', level_es: 'Nuevos sabores', preference: 0.15 },
    ]},
    { attribute: 'Price Tier', attribute_es: 'Precio', levels: [
      { level: 'Mid-Range', level_es: 'Medio', preference: 0.55 },
      { level: 'Value', level_es: 'Económico', preference: 0.30 },
      { level: 'Premium', level_es: 'Premium', preference: 0.15 },
    ]},
  ],
  kids: [
    { attribute: 'Packaging', attribute_es: 'Empaque', levels: [
      { level: 'Fun/Character', level_es: 'Divertido', preference: 0.60 },
      { level: 'Colorful', level_es: 'Colorido', preference: 0.30 },
      { level: 'Standard', level_es: 'Estándar', preference: 0.10 },
    ]},
    { attribute: 'Portion Size', attribute_es: 'Porción', levels: [
      { level: 'Kid-size', level_es: 'Tamaño niño', preference: 0.55 },
      { level: 'Snack-size', level_es: 'Snack', preference: 0.35 },
      { level: 'Regular', level_es: 'Regular', preference: 0.10 },
    ]},
    { attribute: 'Flavor', attribute_es: 'Sabor', levels: [
      { level: 'Chocolate', level_es: 'Chocolate', preference: 0.45 },
      { level: 'Strawberry', level_es: 'Fresa', preference: 0.35 },
      { level: 'Vanilla', level_es: 'Vainilla', preference: 0.20 },
    ]},
  ],
  value: [
    { attribute: 'Price Tier', attribute_es: 'Precio', levels: [
      { level: 'Value/Economy', level_es: 'Económico', preference: 0.60 },
      { level: 'Mid-Range', level_es: 'Medio', preference: 0.30 },
      { level: 'Premium', level_es: 'Premium', preference: 0.10 },
    ]},
    { attribute: 'Pack Size', attribute_es: 'Tamaño', levels: [
      { level: 'Large/Bulk', level_es: 'Grande/Bulk', preference: 0.50 },
      { level: 'Family Size', level_es: 'Familiar', preference: 0.35 },
      { level: 'Single Serve', level_es: 'Individual', preference: 0.15 },
    ]},
    { attribute: 'Brand', attribute_es: 'Marca', levels: [
      { level: 'Store Brand', level_es: 'Marca propia', preference: 0.45 },
      { level: 'Value Brand', level_es: 'Marca económica', preference: 0.35 },
      { level: 'National Brand', level_es: 'Marca nacional', preference: 0.20 },
    ]},
  ],
  health: [
    { attribute: 'Ingredients', attribute_es: 'Ingredientes', levels: [
      { level: 'Organic/Natural', level_es: 'Orgánico/Natural', preference: 0.55 },
      { level: 'Clean Label', level_es: 'Etiqueta limpia', preference: 0.30 },
      { level: 'Standard', level_es: 'Estándar', preference: 0.15 },
    ]},
    { attribute: 'Sugar Content', attribute_es: 'Azúcar', levels: [
      { level: 'No Sugar', level_es: 'Sin azúcar', preference: 0.45 },
      { level: 'Low Sugar', level_es: 'Bajo en azúcar', preference: 0.40 },
      { level: 'Regular', level_es: 'Regular', preference: 0.15 },
    ]},
    { attribute: 'Certifications', attribute_es: 'Certificaciones', levels: [
      { level: 'Organic', level_es: 'Orgánico', preference: 0.40 },
      { level: 'Non-GMO', level_es: 'Sin OGM', preference: 0.35 },
      { level: 'None Required', level_es: 'Sin requisito', preference: 0.25 },
    ]},
  ],
  convenience: [
    { attribute: 'Format', attribute_es: 'Formato', levels: [
      { level: 'Ready-to-Eat', level_es: 'Listo para comer', preference: 0.55 },
      { level: 'Quick Prep', level_es: 'Rápida preparación', preference: 0.30 },
      { level: 'Standard', level_es: 'Estándar', preference: 0.15 },
    ]},
    { attribute: 'Packaging', attribute_es: 'Empaque', levels: [
      { level: 'Resealable', level_es: 'Resellable', preference: 0.45 },
      { level: 'Portable', level_es: 'Portátil', preference: 0.40 },
      { level: 'Standard', level_es: 'Estándar', preference: 0.15 },
    ]},
    { attribute: 'Portion', attribute_es: 'Porción', levels: [
      { level: 'Single Serve', level_es: 'Individual', preference: 0.60 },
      { level: 'Snack Size', level_es: 'Snack', preference: 0.25 },
      { level: 'Multi-pack', level_es: 'Multi-pack', preference: 0.15 },
    ]},
  ],
  indulgence: [
    { attribute: 'Flavor Intensity', attribute_es: 'Intensidad', levels: [
      { level: 'Rich/Intense', level_es: 'Rico/Intenso', preference: 0.55 },
      { level: 'Balanced', level_es: 'Balanceado', preference: 0.30 },
      { level: 'Light', level_es: 'Ligero', preference: 0.15 },
    ]},
    { attribute: 'Quality Tier', attribute_es: 'Calidad', levels: [
      { level: 'Artisan/Craft', level_es: 'Artesanal', preference: 0.50 },
      { level: 'Premium', level_es: 'Premium', preference: 0.35 },
      { level: 'Standard', level_es: 'Estándar', preference: 0.15 },
    ]},
    { attribute: 'Occasion', attribute_es: 'Ocasión', levels: [
      { level: 'Special Treat', level_es: 'Ocasión especial', preference: 0.45 },
      { level: 'Self-reward', level_es: 'Auto-premio', preference: 0.40 },
      { level: 'Everyday', level_es: 'Diario', preference: 0.15 },
    ]},
  ],
  traditional: [
    { attribute: 'Brand Heritage', attribute_es: 'Tradición', levels: [
      { level: 'Heritage Brand', level_es: 'Marca tradicional', preference: 0.55 },
      { level: 'Established', level_es: 'Establecida', preference: 0.30 },
      { level: 'New Brand', level_es: 'Nueva', preference: 0.15 },
    ]},
    { attribute: 'Flavor', attribute_es: 'Sabor', levels: [
      { level: 'Classic', level_es: 'Clásico', preference: 0.60 },
      { level: 'Traditional', level_es: 'Tradicional', preference: 0.30 },
      { level: 'Modern', level_es: 'Moderno', preference: 0.10 },
    ]},
    { attribute: 'Format', attribute_es: 'Formato', levels: [
      { level: 'Original', level_es: 'Original', preference: 0.55 },
      { level: 'Classic Size', level_es: 'Tamaño clásico', preference: 0.35 },
      { level: 'New Format', level_es: 'Nuevo formato', preference: 0.10 },
    ]},
  ],
};

function getAttributePreferencesForSegment(segmentName: string): typeof SEGMENT_ATTRIBUTE_PREFERENCES['premium'] {
  const name = segmentName.toLowerCase();
  if (name.includes('family') || name.includes('familia')) return SEGMENT_ATTRIBUTE_PREFERENCES.family;
  if (name.includes('kid') || name.includes('niño') || name.includes('children')) return SEGMENT_ATTRIBUTE_PREFERENCES.kids;
  if (name.includes('indulgen') || name.includes('treat')) return SEGMENT_ATTRIBUTE_PREFERENCES.indulgence;
  if (name.includes('premium') || name.includes('gourmet') || name.includes('luxury')) return SEGMENT_ATTRIBUTE_PREFERENCES.premium;
  if (name.includes('value') || name.includes('price') || name.includes('budget') || name.includes('económic')) return SEGMENT_ATTRIBUTE_PREFERENCES.value;
  if (name.includes('health') || name.includes('organic') || name.includes('natural') || name.includes('salud')) return SEGMENT_ATTRIBUTE_PREFERENCES.health;
  if (name.includes('convenience') || name.includes('busy') || name.includes('quick') || name.includes('rápid')) return SEGMENT_ATTRIBUTE_PREFERENCES.convenience;
  return SEGMENT_ATTRIBUTE_PREFERENCES.traditional;
}

function ConsumerSection({ clientId, language, subSection }: { clientId: string; language: string; subSection: string }) {
  const { data, isLoading } = useMarketSegments(clientId);
  const { data: marketData } = useMarketOverview(clientId);
  const t = language === 'es';

  if (isLoading) return <LoadingSpinner size="md" />;
  if (!data || !data.segments || data.segments.length === 0) {
    return (
      <div className="text-center py-8 text-text-secondary">
        {t ? 'Datos de segmentos no disponibles' : 'Segment data not available'}
      </div>
    );
  }

  // Show all segments by default (TASK-183: full-picture view)
  const segments = data.segments;
  const brands = marketData?.top_brands || [{ name: 'Generic Brand', share: 0.5 }];

  if (subSection === 'personas') {
    return <PersonasView segments={segments} language={language} />;
  }

  if (subSection === 'compare') {
    return <CompareConsumersView segments={segments} brands={brands} language={language} />;
  }

  if (subSection === 'crosstabs') {
    return <CrossTabsView brands={brands} language={language} />;
  }

  // Transform segments to include all required fields for SegmentsTable
  const transformedSegments = segments.map((seg: any) => ({
    id: seg.id || seg.name.toLowerCase().replace(/\s+/g, '_'),
    name: seg.name,
    description: seg.description,
    share: seg.share,
    growth: seg.growth,
    avgSpend: seg.avg_spend || seg.avgSpend,
    priceIndex: seg.price_index || seg.priceIndex,
    characteristics: seg.characteristics || [],
    topCategories: seg.top_categories || seg.topCategories,
    population: seg.population,
    brand_loyalty: seg.brand_loyalty,
    price_sensitivity: seg.price_sensitivity,
  }));

  return (
    <div className="space-y-4">
      {/* Segments Table with Donut Chart */}
      <SegmentsTable
        segments={transformedSegments}
        language={language}
        onSegmentSelect={(segment) => {
          console.log('Selected segment:', segment.name);
        }}
      />

      {/* Summary insight */}
      <InsightCard
        text={t
          ? `"${segments[0].name}" es el segmento más grande con ${(segments[0].share * 100).toFixed(0)}% del mercado.`
          : `"${segments[0].name}" is the largest segment at ${(segments[0].share * 100).toFixed(0)}% of the market.`}
        type="info"
        sources={[
          { name: 'Kantar Consumer Panel', type: 'kantar', date: '2025', confidence: 0.88 }
        ]}
        language={language}
      />
    </div>
  );
}

// Personas View - Named consumer profiles
function PersonasView({ segments, language }: { segments: any[]; language: string }) {
  const t = language === 'es';

  return (
    <div className="space-y-6">
      <div className="card p-4 bg-surface-700/50">
        <p className="text-sm text-text-secondary">
          {t
            ? 'Perfiles representativos de consumidores basados en investigación de mercado y datos de comportamiento.'
            : 'Representative consumer profiles based on market research and behavioral data.'}
        </p>
      </div>

      {/* Show all segments by default (TASK-183: full-picture view) */}
      {segments.map((segment) => {
        const personas = getPersonasForSegment(segment.name);
        const attributePreferences = getAttributePreferencesForSegment(segment.name);
        return (
          <div key={segment.id} className="card overflow-hidden">
            {/* Segment Header */}
            <div className="p-4 bg-surface-700/50 border-b border-surface-600">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-white">{segment.name}</h3>
                  <p className="text-sm text-text-secondary">
                    {(segment.share * 100).toFixed(0)}% {t ? 'del mercado' : 'of market'} • {segment.characteristics?.slice(0, 2).join(' • ')}
                  </p>
                </div>
                <div className={`text-sm px-3 py-1 rounded-full ${segment.growth >= 0 ? 'bg-success-500/20 text-success-400' : 'bg-danger-500/20 text-danger-400'}`}>
                  {segment.growth >= 0 ? '+' : ''}{(segment.growth * 100).toFixed(1)}%
                </div>
              </div>
            </div>

            {/* Single Representative Persona */}
            <div className="p-4">
              {(() => {
                const persona = personas[0]; // Show only 1 representative persona
                return (
                  <div className="flex items-start gap-4">
                    <div className="text-4xl">{persona.avatar}</div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-semibold text-white">{persona.name}</span>
                        <span className="text-xs text-text-tertiary">({persona.age} {t ? 'años' : 'yrs'}, {persona.occupation})</span>
                      </div>
                      <blockquote className="text-sm text-text-secondary italic border-l-2 border-primary-500/50 pl-3 my-2">
                        "{persona.quote}"
                      </blockquote>
                      <div className="flex flex-wrap gap-1.5 mt-3">
                        {segment.characteristics?.slice(0, 4).map((char: string, i: number) => (
                          <span key={i} className="text-xs px-2 py-1 bg-surface-600 text-text-secondary rounded">
                            {char}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                );
              })()}
            </div>

            {/* Attribute Preferences - condensed summary */}
            {attributePreferences && attributePreferences.length > 0 && (
              <div className="px-4 pb-4 pt-0">
                <div className="text-xs text-text-tertiary">
                  {t ? 'Prioridades: ' : 'Priorities: '}
                  {attributePreferences.slice(0, 3).map((attr, i) => {
                    const topLevel = attr.levels.reduce((a, b) => a.preference > b.preference ? a : b);
                    return (
                      <span key={i}>
                        <span className="text-text-secondary">{t ? attr.attribute_es : attr.attribute}</span>
                        {' → '}
                        <span className="text-primary-400">{t ? topLevel.level_es : topLevel.level}</span>
                        {i < 2 && ' • '}
                      </span>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// Compare Consumers View - Side-by-side profile builders
function CompareConsumersView({
  segments,
  brands,
  language,
}: {
  segments: any[];
  brands: Array<{ name: string; share: number }>;
  language: string;
}) {
  const t = language === 'es';

  // Transform segments to match the Segment interface
  const transformedSegments: Segment[] = useMemo(() => {
    return segments.map((seg: any) => ({
      id: seg.id || seg.name.toLowerCase().replace(/\s+/g, '_'),
      name: seg.name,
      share: seg.share,
      characteristics: seg.characteristics || [],
      price_sensitivity: seg.price_sensitivity ?? 0.5,
      brand_loyalty: seg.brand_loyalty ?? 0.5,
      demographics: {
        age_range: '25-55',
        income: 'C',
        geography: 'National',
      },
    }));
  }, [segments]);

  // Profile generation hook
  const { generateProfile, compareProfiles } = useProfileGeneration(transformedSegments, language);

  // State for both profiles
  const [profileA, setProfileA] = useState<DerivedPersona | null>(null);
  const [profileB, setProfileB] = useState<DerivedPersona | null>(null);

  // Handle profile generation
  const handleGenerateA = (selections: ProfileSelections) => {
    if (transformedSegments.length > 0) {
      setProfileA(generateProfile(selections));
    }
  };

  const handleGenerateB = (selections: ProfileSelections) => {
    if (transformedSegments.length > 0) {
      setProfileB(generateProfile(selections));
    }
  };

  // Calculate comparison when both profiles exist
  const comparison = useMemo(() => {
    if (profileA && profileB) {
      return compareProfiles(profileA, profileB);
    }
    return null;
  }, [profileA, profileB, compareProfiles]);

  return (
    <div className="space-y-6">
      {/* Intro */}
      <div className="card p-4 bg-surface-700/50">
        <p className="text-sm text-text-secondary">
          {t
            ? 'Crea y compara dos perfiles de consumidor para entender diferencias en comportamiento y preferencias.'
            : 'Create and compare two consumer profiles to understand differences in behavior and preferences.'}
        </p>
      </div>

      {/* Split Screen - Profile Builders */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Profile A */}
        <div className="space-y-4">
          <ProfileBuilder
            side="A"
            brands={brands}
            onGenerate={handleGenerateA}
            language={language}
          />
          {profileA && (
            <DerivedPersonaCard persona={profileA} language={language} />
          )}
        </div>

        {/* Profile B */}
        <div className="space-y-4">
          <ProfileBuilder
            side="B"
            brands={brands}
            onGenerate={handleGenerateB}
            language={language}
          />
          {profileB && (
            <DerivedPersonaCard persona={profileB} language={language} />
          )}
        </div>
      </div>

      {/* VS Badge (centered) */}
      {(profileA || profileB) && (
        <div className="flex justify-center -mt-2 mb-4">
          <span className="px-3 py-1 bg-surface-700 rounded-full text-xs font-medium text-text-secondary">
            VS
          </span>
        </div>
      )}

      {/* Comparison Summary */}
      {comparison && (
        <ComparisonSummary comparison={comparison} language={language} />
      )}

      {/* Empty state hint */}
      {!profileA && !profileB && (
        <div className="card p-8 text-center bg-surface-800/30">
          <p className="text-text-tertiary text-sm">
            {t
              ? 'Configura los perfiles arriba y haz clic en "Generar Perfil" para ver la comparación.'
              : 'Configure the profiles above and click "Generate Profile" to see the comparison.'}
          </p>
        </div>
      )}
    </div>
  );
}

// VoterPulse-style expandable segment card
function SegmentCard({
  segment,
  rank,
  language,
  defaultExpanded = false,
  compact = false,
}: {
  segment: {
    id: string;
    name: string;
    share: number;
    growth: number;
    characteristics: string[];
    price_sensitivity?: number;
    brand_loyalty?: number;
    population?: number;
  };
  rank: number;
  language: string;
  defaultExpanded?: boolean;
  compact?: boolean;
}) {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const t = language === 'es';

  const sharePercent = (segment.share * 100).toFixed(0);
  const loyaltyPercent = segment.brand_loyalty ? (segment.brand_loyalty * 100).toFixed(0) : null;
  const growthPercent = (segment.growth * 100).toFixed(1);
  const personas = getPersonasForSegment(segment.name);
  const attributePreferences = getAttributePreferencesForSegment(segment.name);

  // Format population with K/M suffix
  const formatPopulation = (pop: number | undefined) => {
    if (!pop) return t ? '—' : '—';
    if (pop >= 1000000) return `${(pop / 1000000).toFixed(1)}M`;
    if (pop >= 1000) return `${(pop / 1000).toFixed(0)}K`;
    return pop.toString();
  };

  // Price sensitivity label
  const getPriceSensLabel = (sens: number | undefined) => {
    if (sens === undefined) return t ? '—' : '—';
    if (sens < 0.3) return t ? 'Baja' : 'Low';
    if (sens < 0.6) return t ? 'Media' : 'Medium';
    return t ? 'Alta' : 'High';
  };

  // Segment color based on rank
  const segmentColors = [
    { bg: 'bg-primary-500/20', text: 'text-primary-400', border: 'border-primary-500/30', bar: 'bg-primary-500' },
    { bg: 'bg-amber-500/20', text: 'text-amber-400', border: 'border-amber-500/30', bar: 'bg-amber-500' },
    { bg: 'bg-emerald-500/20', text: 'text-emerald-400', border: 'border-emerald-500/30', bar: 'bg-emerald-500' },
    { bg: 'bg-purple-500/20', text: 'text-purple-400', border: 'border-purple-500/30', bar: 'bg-purple-500' },
  ];
  const colorScheme = segmentColors[(rank - 1) % segmentColors.length];

  // Compact card view for grid layout
  if (compact && !isExpanded) {
    return (
      <div className="card overflow-hidden transition-all duration-300 hover:border-surface-500">
        <button
          onClick={() => setIsExpanded(true)}
          className="w-full text-left p-4 hover:bg-surface-600/30 cursor-pointer transition-colors"
        >
          {/* Header with rank and name */}
          <div className="flex items-center gap-3 mb-3">
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 border text-xs font-bold ${colorScheme.bg} ${colorScheme.text} ${colorScheme.border}`}>
              #{rank}
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-white text-sm truncate">{segment.name}</h3>
            </div>
            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
              segment.growth >= 0 ? 'bg-success-500/20 text-success-400' : 'bg-danger-500/20 text-danger-400'
            }`}>
              {segment.growth >= 0 ? '▲' : '▼'} {Math.abs(Number(growthPercent))}%
            </span>
          </div>

          {/* Share bar */}
          <div className="mb-3">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-text-tertiary" title={t ? 'Participación en valor del mercado' : 'Value share of market'}>{t ? 'Participación (Valor)' : 'Value Share'}</span>
              <span className="text-xs font-semibold text-white">{sharePercent}%</span>
            </div>
            <div className="h-2 bg-surface-600 rounded-full overflow-hidden">
              <div
                className={`h-full ${colorScheme.bar} rounded-full`}
                style={{ width: `${Math.min(Number(sharePercent), 100)}%` }}
              />
            </div>
          </div>

          {/* Key metrics */}
          <div className="flex items-center justify-between text-xs">
            <div className="flex items-center gap-1">
              <span>🔄</span>
              <span className="text-primary-400 font-medium">{loyaltyPercent || '—'}%</span>
              <span className="text-text-tertiary">{t ? 'lealtad' : 'loyalty'}</span>
            </div>
            <div className="flex items-center gap-1 text-text-tertiary">
              <span>👥</span>
              <span>{formatPopulation(segment.population)}</span>
            </div>
          </div>

          {/* Expand hint */}
          <div className="mt-3 pt-2 border-t border-surface-600/50 text-center">
            <span className="text-xs text-text-tertiary">{t ? 'Click para expandir' : 'Click to expand'}</span>
          </div>
        </button>
      </div>
    );
  }

  // Recommended actions based on segment characteristics
  const getRecommendedActions = () => {
    const actions = [];
    if (segment.brand_loyalty && segment.brand_loyalty > 0.7) {
      actions.push({
        icon: '🎯',
        text: t ? 'Reforzar programas de fidelización' : 'Strengthen loyalty programs',
      });
    }
    if (segment.price_sensitivity && segment.price_sensitivity < 0.3) {
      actions.push({
        icon: '💎',
        text: t ? 'Explorar oportunidades premium' : 'Explore premium opportunities',
      });
    }
    if (segment.growth > 0.03) {
      actions.push({
        icon: '📈',
        text: t ? 'Aumentar inversión en este segmento' : 'Increase investment in this segment',
      });
    }
    if (segment.growth < 0) {
      actions.push({
        icon: '🔍',
        text: t ? 'Investigar causas de declive' : 'Investigate decline causes',
      });
    }
    if (actions.length === 0) {
      actions.push({
        icon: '📊',
        text: t ? 'Monitorear tendencias de mercado' : 'Monitor market trends',
      });
    }
    return actions.slice(0, 3);
  };

  return (
    <div className="card overflow-hidden transition-all duration-300">
      {/* Compact Header - Always visible */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full text-left hover:bg-surface-600/30 cursor-pointer transition-colors"
      >
        {/* Top row: Rank, Name, Growth badge */}
        <div className="p-4 pb-2">
          <div className="flex items-start gap-3">
            {/* Rank Badge */}
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 border ${colorScheme.bg} ${colorScheme.text} ${colorScheme.border}`}>
              <span className="font-bold text-sm">#{rank}</span>
            </div>

            {/* Name & Growth */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-semibold text-white text-lg">{segment.name}</span>
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                  segment.growth >= 0 ? 'bg-success-500/20 text-success-400' : 'bg-danger-500/20 text-danger-400'
                }`}>
                  {segment.growth >= 0 ? '▲' : '▼'} {segment.growth >= 0 ? '+' : ''}{growthPercent}%
                </span>
              </div>
            </div>

            {/* Expand indicator */}
            <svg
              className={`w-5 h-5 text-text-secondary transition-transform duration-300 flex-shrink-0 ${isExpanded ? 'rotate-180' : ''}`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
            </svg>
          </div>

          {/* Share progress bar */}
          <div className="mt-3 ml-13">
            <div className="flex items-center gap-3">
              <div className="flex-1 h-3 bg-surface-600 rounded-full overflow-hidden">
                <div
                  className={`h-full ${colorScheme.bar} rounded-full transition-all duration-500`}
                  style={{ width: `${Math.min(Number(sharePercent), 100)}%` }}
                />
              </div>
              <span className="text-sm font-semibold text-white w-14 text-right">{sharePercent}% {t ? 'share' : 'share'}</span>
            </div>
          </div>
        </div>

        {/* 3-column metrics row with icons */}
        <div className="px-4 pb-3">
          <div className="flex items-center gap-2 py-2 px-3 bg-surface-700/30 rounded-lg">
            <div className="flex items-center gap-1.5 flex-1">
              <span className="text-sm">👥</span>
              <span className="text-xs text-text-secondary">{formatPopulation(segment.population)}</span>
              <span className="text-xs text-text-tertiary">{t ? 'consumidores' : 'consumers'}</span>
            </div>
            <div className="w-px h-4 bg-surface-500/50" />
            <div className="flex items-center gap-1.5 flex-1">
              <span className="text-sm">💰</span>
              <span className={`text-xs ${segment.price_sensitivity && segment.price_sensitivity < 0.3 ? 'text-success-400' : segment.price_sensitivity && segment.price_sensitivity > 0.6 ? 'text-warning-400' : 'text-text-secondary'}`}>
                {getPriceSensLabel(segment.price_sensitivity)}
              </span>
              <span className="text-xs text-text-tertiary">{t ? 'sens. precio' : 'price sens'}</span>
            </div>
            <div className="w-px h-4 bg-surface-500/50" />
            <div className="flex items-center gap-1.5 flex-1">
              <span className="text-sm">🔄</span>
              <span className="text-xs text-primary-400 font-medium">{loyaltyPercent || '—'}%</span>
              <span className="text-xs text-text-tertiary">{t ? 'lealtad' : 'loyalty'}</span>
            </div>
          </div>
        </div>

        {/* Characteristic tags - visible in compact view */}
        {segment.characteristics && segment.characteristics.length > 0 && (
          <div className="px-4 pb-3">
            <div className="flex flex-wrap gap-1.5">
              {segment.characteristics.slice(0, 4).map((char, i) => (
                <span key={i} className="text-xs px-2 py-0.5 bg-surface-600/70 text-text-secondary rounded-full">
                  {char}
                </span>
              ))}
              {segment.characteristics.length > 4 && (
                <span className="text-xs px-2 py-0.5 text-text-tertiary">
                  +{segment.characteristics.length - 4} {t ? 'más' : 'more'}
                </span>
              )}
            </div>
          </div>
        )}
      </button>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="border-t border-surface-400/15 p-4 space-y-4 bg-surface-800/30 animate-in slide-in-from-top-2 duration-200">
          {/* Sample Persona Preview */}
          {personas[0] && (
            <div className="p-3 bg-surface-700/50 rounded-lg flex items-center gap-3">
              <span className="text-3xl">{personas[0].avatar}</span>
              <div className="flex-1">
                <div className="text-sm font-medium text-white">{personas[0].name}</div>
                <div className="text-xs text-text-secondary italic">"{personas[0].quote}"</div>
              </div>
              {personas.length > 1 && (
                <span className="text-xs text-text-tertiary">+{personas.length - 1} {t ? 'más' : 'more'}</span>
              )}
            </div>
          )}

          {/* Detailed Metrics Grid */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="p-3 bg-surface-600/50 rounded-lg text-center">
              <div className="font-semibold text-white">{sharePercent}%</div>
              <div className="text-xs text-text-tertiary" title={t ? 'Participación en valor del mercado' : 'Value share of market'}>{t ? 'Participación (Valor)' : 'Value Share'}</div>
            </div>
            <div className="p-3 bg-surface-600/50 rounded-lg text-center">
              <div className={`font-semibold ${segment.growth >= 0 ? 'text-success-400' : 'text-danger-400'}`}>
                {segment.growth >= 0 ? '+' : ''}{growthPercent}%
              </div>
              <div className="text-xs text-text-tertiary">{t ? 'Crecimiento' : 'Growth'}</div>
            </div>
            <div className="p-3 bg-surface-600/50 rounded-lg text-center">
              <div className="font-semibold text-primary-400">{loyaltyPercent || '—'}%</div>
              <div className="text-xs text-text-tertiary">{t ? 'Lealtad' : 'Loyalty'}</div>
            </div>
            <div className="p-3 bg-surface-600/50 rounded-lg text-center">
              <div className="font-semibold text-white">{formatPopulation(segment.population)}</div>
              <div className="text-xs text-text-tertiary">{t ? 'Población' : 'Population'}</div>
            </div>
          </div>

          {/* Price Sensitivity Bar */}
          {segment.price_sensitivity !== undefined && (
            <div>
              <div className="flex justify-between items-center mb-1">
                <span className="text-xs text-text-secondary">{t ? 'Sensibilidad al Precio' : 'Price Sensitivity'}</span>
                <span className="text-xs text-text-secondary">{(segment.price_sensitivity * 10).toFixed(1)}/10</span>
              </div>
              <div className="h-2 bg-surface-600 rounded-full overflow-hidden">
                <div
                  className="h-full bg-warning-500 rounded-full transition-all"
                  style={{ width: `${Math.min(segment.price_sensitivity * 100, 100)}%` }}
                />
              </div>
            </div>
          )}

          {/* Recommended Actions */}
          <div>
            <h5 className="text-xs font-medium text-text-secondary uppercase tracking-wider mb-2">
              {t ? 'Acciones Recomendadas' : 'Recommended Actions'}
            </h5>
            <div className="space-y-2">
              {getRecommendedActions().map((action, i) => (
                <div key={i} className="flex items-center gap-2 p-2 bg-surface-700/30 rounded-lg">
                  <span>{action.icon}</span>
                  <span className="text-sm text-text-secondary">{action.text}</span>
                </div>
              ))}
            </div>
          </div>

          {/* All Characteristics Tags */}
          {segment.characteristics && segment.characteristics.length > 0 && (
            <div>
              <h5 className="text-xs font-medium text-text-secondary uppercase tracking-wider mb-2">
                {t ? 'Todas las Características' : 'All Characteristics'}
              </h5>
              <div className="flex flex-wrap gap-2">
                {segment.characteristics.map((char, i) => (
                  <span key={i} className="text-xs px-2.5 py-1 bg-surface-600 text-text-secondary rounded-full">
                    {char}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Attribute/Level Preferences */}
          {attributePreferences && attributePreferences.length > 0 && (
            <div>
              <h5 className="text-xs font-medium text-text-secondary uppercase tracking-wider mb-3">
                {t ? 'Preferencias por Atributo' : 'Attribute Preferences'}
              </h5>
              <div className="space-y-3">
                {attributePreferences.map((attr, attrIdx) => (
                  <div key={attrIdx} className="bg-surface-700/30 rounded-lg p-3">
                    <div className="text-xs font-medium text-text-secondary mb-2">
                      {t ? attr.attribute_es : attr.attribute}
                    </div>
                    <div className="space-y-1.5">
                      {attr.levels.map((level, levelIdx) => {
                        // Color gradient based on preference strength
                        const barColor = level.preference >= 0.5
                          ? 'bg-primary-500'
                          : level.preference >= 0.3
                            ? 'bg-primary-500/70'
                            : 'bg-primary-500/40';
                        return (
                          <div key={levelIdx} className="flex items-center gap-2">
                            <div className="w-24 text-xs text-text-secondary truncate">
                              {t ? level.level_es : level.level}
                            </div>
                            <div className="flex-1 h-4 bg-surface-600 rounded overflow-hidden">
                              <div
                                className={`h-full ${barColor} rounded transition-all duration-300`}
                                style={{ width: `${level.preference * 100}%` }}
                              />
                            </div>
                            <div className="w-10 text-xs text-text-secondary text-right">
                              {(level.preference * 100).toFixed(0)}%
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// CHANNEL SECTION - Distribution vs Retail views
// ============================================================================

// Channel data with subchannels and characteristics
const CHANNEL_DATA = {
  modern_trade: {
    id: 'modern_trade',
    icon: '🏬',
    color: 'primary',
    characteristics: {
      en: ['High visibility', 'Premium shelf space', 'Strong promotions', 'Price competition'],
      es: ['Alta visibilidad', 'Espacio premium', 'Promociones fuertes', 'Competencia de precio'],
    },
    subchannels: [
      { id: 'hypermarket', name: { en: 'Hypermarkets', es: 'Hipermercados' }, share: 0.18, examples: ['Walmart', 'Chedraui', 'Soriana'] },
      { id: 'supermarket', name: { en: 'Supermarkets', es: 'Supermercados' }, share: 0.15, examples: ['La Comer', 'HEB', 'Superama'] },
      { id: 'warehouse', name: { en: 'Warehouse Clubs', es: 'Clubes de Precio' }, share: 0.08, examples: ['Costco', 'Sams Club', 'City Club'] },
      { id: 'discounter', name: { en: 'Discounters', es: 'Tiendas de Descuento' }, share: 0.04, examples: ['Bodega Aurrera', 'Tiendas 3B'] },
    ],
  },
  traditional: {
    id: 'traditional',
    icon: '🏪',
    color: 'amber',
    characteristics: {
      en: ['High fragmentation', 'Personal relationships', 'Cash payments', 'Limited SKUs'],
      es: ['Alta fragmentación', 'Relaciones personales', 'Pagos en efectivo', 'SKUs limitados'],
    },
    subchannels: [
      { id: 'tiendita', name: { en: 'Corner Stores', es: 'Tienditas' }, share: 0.25, examples: ['Abarrotes', 'Misceláneas'] },
      { id: 'mercado', name: { en: 'Public Markets', es: 'Mercados Públicos' }, share: 0.08, examples: ['Mercados municipales'] },
      { id: 'mayorista', name: { en: 'Wholesalers', es: 'Mayoristas' }, share: 0.07, examples: ['Central de Abastos'] },
    ],
  },
  convenience: {
    id: 'convenience',
    icon: '⛽',
    color: 'emerald',
    characteristics: {
      en: ['Impulse purchases', 'Premium pricing', 'Extended hours', 'High traffic'],
      es: ['Compras impulsivas', 'Precios premium', 'Horarios extendidos', 'Alto tráfico'],
    },
    subchannels: [
      { id: 'cstore', name: { en: 'Convenience Stores', es: 'Tiendas de Conveniencia' }, share: 0.07, examples: ['OXXO', '7-Eleven', 'Circle K'] },
      { id: 'gas_station', name: { en: 'Gas Stations', es: 'Gasolineras' }, share: 0.03, examples: ['Pemex Shops', 'Mobil'] },
    ],
  },
  ecommerce: {
    id: 'ecommerce',
    icon: '📱',
    color: 'purple',
    characteristics: {
      en: ['Fastest growing', 'Data-rich', 'Home delivery', 'Subscription potential'],
      es: ['Más rápido crecimiento', 'Rico en datos', 'Entrega a domicilio', 'Potencial de suscripción'],
    },
    subchannels: [
      { id: 'marketplace', name: { en: 'Marketplaces', es: 'Marketplaces' }, share: 0.025, examples: ['Amazon', 'Mercado Libre'] },
      { id: 'delivery', name: { en: 'Delivery Apps', es: 'Apps de Delivery' }, share: 0.015, examples: ['Rappi', 'Uber Eats', 'Cornershop'] },
      { id: 'dth', name: { en: 'Direct to Home', es: 'Directo a Casa' }, share: 0.01, examples: ['Brand websites', 'Subscriptions'] },
    ],
  },
};

function ChannelSection({ clientId, language, subSection }: { clientId: string; language: string; subSection: string }) {
  const t = language === 'es';

  // Fetch channel data from API
  const { data: channelData, isLoading } = useQuery({
    queryKey: ['client', clientId, 'market', 'channels'],
    queryFn: () => apiClient.market.getChannels(clientId),
    enabled: !!clientId,
  });

  if (isLoading) return <LoadingSpinner size="md" />;

  // Merge API data with our enhanced channel info
  const apiChannels = channelData?.channels || [];
  const channels = [
    {
      ...CHANNEL_DATA.modern_trade,
      name: t ? 'Comercio Moderno' : 'Modern Trade',
      share: apiChannels.find((c: any) => c.id === 'modern_trade')?.share || 0.45,
      growth: apiChannels.find((c: any) => c.id === 'modern_trade')?.growth || 0.03,
    },
    {
      ...CHANNEL_DATA.traditional,
      name: t ? 'Comercio Tradicional' : 'Traditional Trade',
      share: apiChannels.find((c: any) => c.id === 'traditional')?.share || 0.40,
      growth: apiChannels.find((c: any) => c.id === 'traditional')?.growth || -0.01,
    },
    {
      ...CHANNEL_DATA.convenience,
      name: t ? 'Conveniencia' : 'Convenience',
      share: apiChannels.find((c: any) => c.id === 'convenience')?.share || 0.10,
      growth: apiChannels.find((c: any) => c.id === 'convenience')?.growth || 0.05,
    },
    {
      ...CHANNEL_DATA.ecommerce,
      name: 'E-Commerce',
      share: apiChannels.find((c: any) => c.id === 'online')?.share || 0.05,
      growth: apiChannels.find((c: any) => c.id === 'online')?.growth || 0.15,
    },
  ];

  // Distribution metrics from API or defaults
  const distribution = channelData?.distribution || {
    weighted_distribution: 0.70,
    numeric_distribution: 0.45,
    out_of_stock_rate: 0.08,
  };

  const tradeDynamics = channelData?.trade_dynamics || {
    average_margin: 0.22,
    promo_intensity: 0.35,
    private_label_share: 0.12,
  };

  // Render Sell-In/Sell-Out flow map (Phase 4)
  if (subSection === 'sell-flow') {
    // Transform channel data to ChannelMap format with sell-in/sell-out metrics
    const channelFlowData = channels.map((ch) => {
      // Generate realistic sell-in/sell-out based on channel share and growth
      const baseValue = (ch.share || 0.1) * 10000; // Scale to reasonable values
      const sellIn = baseValue;
      const sellOut = ch.growth >= 0 ? baseValue * 0.95 : baseValue * 1.05; // Growing channels stock up

      return {
        id: ch.id,
        name: ch.name,
        nameEs: ch.name, // Use same since it's already localized
        icon: ch.icon,
        color: ch.color === 'amber' ? 'warning' : ch.color === 'emerald' ? 'success' : ch.color,
        sellIn: Math.round(sellIn),
        sellOut: Math.round(sellOut),
        sellInGrowth: ch.growth,
        sellOutGrowth: ch.growth * 0.9, // Slightly different growth
        share: ch.share,
        subchannels: ch.subchannels?.map((sub) => {
          const subBaseValue = sub.share * 10000;
          return {
            id: sub.id,
            name: typeof sub.name === 'object' ? sub.name.en : sub.name,
            nameEs: typeof sub.name === 'object' ? sub.name.es : sub.name,
            sellIn: Math.round(subBaseValue),
            sellOut: Math.round(subBaseValue * (ch.growth >= 0 ? 0.95 : 1.05)),
          };
        }),
      };
    });

    return (
      <div className="space-y-4">
        <SectionHeaderWithFreshness
          title={t ? 'Flujo de Canal' : 'Channel Flow'}
          provenance={PROVENANCE_PRESETS.nielsenRecent(new Date('2023-09-30'))}
          locale={language as 'en' | 'es'}
        />
        <ChannelMap
          channels={channelFlowData}
          language={language as 'en' | 'es'}
          currency="$"
          unit="K"
        />
        <InsightCard
          text={t
            ? 'La brecha entre Sell-In y Sell-Out indica niveles de inventario en canal. Una brecha positiva significa acumulación de stock; negativa indica demanda mayor que oferta.'
            : 'The gap between Sell-In and Sell-Out indicates channel inventory levels. A positive gap means stock accumulation; negative indicates demand exceeding supply.'}
          type="info"
        />
      </div>
    );
  }

  // Render Distribution sub-section (metrics-focused)
  if (subSection === 'distribution') {
    return <DistributionMetricsView distribution={distribution} tradeDynamics={tradeDynamics} language={language} />;
  }

  // Default: Retail sub-section (channel breakdown with subchannels)
  return <RetailChannelsView channels={channels} language={language} />;
}

// Distribution Metrics View - KPIs and trade dynamics
function DistributionMetricsView({
  distribution,
  tradeDynamics,
  language,
}: {
  distribution: { weighted_distribution: number; numeric_distribution: number; out_of_stock_rate: number };
  tradeDynamics: { average_margin: number; promo_intensity: number; private_label_share: number };
  language: string;
}) {
  const t = language === 'es';

  return (
    <div className="space-y-6">
      {/* Data Source Indicator */}
      <SectionHeaderWithFreshness
        title={t ? 'Métricas de Distribución' : 'Distribution Metrics'}
        provenance={PROVENANCE_PRESETS.nielsenRecent(new Date('2023-09-30'))}
        locale={language as 'en' | 'es'}
      />

      {/* Distribution KPIs */}
      <div className="grid grid-cols-3 gap-4">
        <div className="card p-6 text-center">
          <div className="text-xs text-text-tertiary uppercase tracking-wider mb-2">
            {t ? 'Distribución Ponderada' : 'Weighted Distribution'}
          </div>
          <div className="text-3xl font-bold text-primary-400">
            {(distribution.weighted_distribution * 100).toFixed(0)}%
          </div>
          <div className="text-xs text-text-tertiary mt-1">
            {t ? 'de ventas del mercado' : 'of market sales'}
          </div>
        </div>
        <div className="card p-6 text-center">
          <div className="text-xs text-text-tertiary uppercase tracking-wider mb-2">
            {t ? 'Distribución Numérica' : 'Numeric Distribution'}
          </div>
          <div className="text-3xl font-bold text-white">
            {(distribution.numeric_distribution * 100).toFixed(0)}%
          </div>
          <div className="text-xs text-text-tertiary mt-1">
            {t ? 'de puntos de venta' : 'of stores'}
          </div>
        </div>
        <div className="card p-6 text-center">
          <div className="text-xs text-text-tertiary uppercase tracking-wider mb-2">
            {t ? 'Tasa de Agotados' : 'Out of Stock Rate'}
          </div>
          <div className={`text-3xl font-bold ${distribution.out_of_stock_rate > 0.1 ? 'text-danger-400' : 'text-success-400'}`}>
            {(distribution.out_of_stock_rate * 100).toFixed(1)}%
          </div>
          <div className="text-xs text-text-tertiary mt-1">
            {t ? 'promedio' : 'average'}
          </div>
        </div>
      </div>

      {/* Distribution Quality Gauge */}
      <div className="card p-5">
        <h3 className="text-sm font-medium text-text-secondary uppercase tracking-wider mb-4">
          {t ? 'Calidad de Distribución' : 'Distribution Quality'}
        </h3>
        <div className="space-y-4">
          {/* Weighted vs Numeric comparison */}
          <div className="flex items-center gap-4">
            <div className="w-32 text-sm text-text-secondary">{t ? 'Ponderada' : 'Weighted'}</div>
            <div className="flex-1 h-4 bg-surface-600 rounded-full overflow-hidden">
              <div
                className="h-full bg-primary-500 rounded-full"
                style={{ width: `${distribution.weighted_distribution * 100}%` }}
              />
            </div>
            <div className="w-16 text-right text-sm font-medium text-white">
              {(distribution.weighted_distribution * 100).toFixed(0)}%
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="w-32 text-sm text-text-secondary">{t ? 'Numérica' : 'Numeric'}</div>
            <div className="flex-1 h-4 bg-surface-600 rounded-full overflow-hidden">
              <div
                className="h-full bg-amber-500 rounded-full"
                style={{ width: `${distribution.numeric_distribution * 100}%` }}
              />
            </div>
            <div className="w-16 text-right text-sm font-medium text-white">
              {(distribution.numeric_distribution * 100).toFixed(0)}%
            </div>
          </div>
        </div>
        <div className="mt-4 p-3 bg-surface-700/50 rounded-lg">
          <p className="text-sm text-text-secondary">
            {distribution.weighted_distribution > distribution.numeric_distribution * 1.3
              ? (t ? '✓ Excelente: Estás en las tiendas que más venden.' : '✓ Excellent: You\'re in the stores that sell the most.')
              : (t ? '⚠ Oportunidad: Mejorar presencia en tiendas de alto volumen.' : '⚠ Opportunity: Improve presence in high-volume stores.')}
          </p>
        </div>
      </div>

      {/* Trade Dynamics */}
      <div className="card p-5">
        <h3 className="text-sm font-medium text-text-secondary uppercase tracking-wider mb-4">
          {t ? 'Dinámica del Canal' : 'Trade Dynamics'}
        </h3>
        <div className="grid grid-cols-3 gap-4">
          <div className="p-4 bg-surface-700/50 rounded-lg text-center">
            <div className="text-2xl font-bold text-white mb-1">
              {(tradeDynamics.average_margin * 100).toFixed(0)}%
            </div>
            <div className="text-xs text-text-tertiary">
              {t ? 'Margen Retailer Prom.' : 'Avg Retailer Margin'}
            </div>
          </div>
          <div className="p-4 bg-surface-700/50 rounded-lg text-center">
            <div className="text-2xl font-bold text-warning-400 mb-1">
              {(tradeDynamics.promo_intensity * 100).toFixed(0)}%
            </div>
            <div className="text-xs text-text-tertiary">
              {t ? 'Volumen en Promoción' : 'Promo Volume'}
            </div>
          </div>
          <div className="p-4 bg-surface-700/50 rounded-lg text-center">
            <div className="text-2xl font-bold text-purple-400 mb-1">
              {(tradeDynamics.private_label_share * 100).toFixed(0)}%
            </div>
            <div className="text-xs text-text-tertiary">
              {t ? 'Marca Privada' : 'Private Label Share'}
            </div>
          </div>
        </div>
      </div>

      {/* Insight */}
      <InsightCard
        text={distribution.out_of_stock_rate > 0.08
          ? (t ? `Tasa de agotados de ${(distribution.out_of_stock_rate * 100).toFixed(1)}% está costando ventas. Cada 1% de mejora = ~0.5% más ventas.` : `Out-of-stock rate of ${(distribution.out_of_stock_rate * 100).toFixed(1)}% is costing sales. Every 1% improvement = ~0.5% more sales.`)
          : (t ? `Buena ejecución en punto de venta con ${(distribution.out_of_stock_rate * 100).toFixed(1)}% de agotados.` : `Good in-store execution with ${(distribution.out_of_stock_rate * 100).toFixed(1)}% out-of-stock rate.`)}
        type={distribution.out_of_stock_rate > 0.08 ? 'warning' : 'success'}
      />
    </div>
  );
}

// Retail Channels View - Channel breakdown with subchannels
function RetailChannelsView({
  channels,
  language,
}: {
  channels: Array<{
    id: string;
    name: string;
    icon: string;
    color: string;
    share: number;
    growth: number;
    characteristics: { en: string[]; es: string[] };
    subchannels: Array<{ id: string; name: { en: string; es: string }; share: number; examples: string[] }>;
  }>;
  language: string;
}) {
  const t = language === 'es';
  const [expandedChannel, setExpandedChannel] = useState<string | null>('modern_trade');

  const colorMap: Record<string, string> = {
    primary: 'bg-primary-500',
    amber: 'bg-amber-500',
    emerald: 'bg-emerald-500',
    purple: 'bg-purple-500',
  };

  const bgColorMap: Record<string, string> = {
    primary: 'bg-primary-500/20 text-primary-400 border-primary-500/30',
    amber: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
    emerald: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
    purple: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
  };

  const fastestGrowing = [...channels].sort((a, b) => b.growth - a.growth)[0];

  return (
    <div className="space-y-4">
      {/* Data Source Indicator */}
      <SectionHeaderWithFreshness
        title={t ? 'Canales de Venta' : 'Sales Channels'}
        provenance={PROVENANCE_PRESETS.nielsenRecent(new Date('2023-09-30'))}
        locale={language as 'en' | 'es'}
      />

      {/* Channel Cards with Subchannel expansion */}
      {channels.map((channel) => {
        const isExpanded = expandedChannel === channel.id;
        return (
          <div key={channel.id} className="card overflow-hidden">
            {/* Channel Header */}
            <button
              onClick={() => setExpandedChannel(isExpanded ? null : channel.id)}
              className="w-full p-4 text-left hover:bg-surface-600/30 transition-colors"
            >
              <div className="flex items-center gap-4">
                <div className={`w-12 h-12 rounded-lg flex items-center justify-center border ${bgColorMap[channel.color]}`}>
                  <span className="text-2xl">{channel.icon}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-semibold text-white">{channel.name}</span>
                    <span className={`text-xs px-2 py-0.5 rounded ${
                      channel.growth >= 0 ? 'bg-success-500/20 text-success-400' : 'bg-danger-500/20 text-danger-400'
                    }`} title={t ? 'Crecimiento año contra año' : 'Year-over-year growth'}>
                      {channel.growth >= 0 ? '+' : ''}{(channel.growth * 100).toFixed(0)}% {t ? 'YoY' : 'YoY'}
                    </span>
                  </div>
                  <div className="text-sm text-text-secondary">
                    {channel.subchannels.length} {t ? 'subcanales' : 'subchannels'}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-2xl font-bold text-white">{(channel.share * 100).toFixed(0)}%</div>
                  <div className="text-xs text-text-tertiary">{t ? 'valor de ventas' : 'value share'}</div>
                </div>
                <svg
                  className={`w-5 h-5 text-text-secondary transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </button>

            {/* Expanded Content: Subchannels & Characteristics */}
            {isExpanded && (
              <div className="border-t border-surface-600 bg-surface-800/50">
                {/* Characteristics */}
                <div className="p-4 border-b border-surface-600">
                  <h4 className="text-xs font-medium text-text-tertiary uppercase tracking-wider mb-2">
                    {t ? 'Características del Canal' : 'Channel Characteristics'}
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {(t ? channel.characteristics.es : channel.characteristics.en).map((char, i) => (
                      <span key={i} className="text-xs px-2.5 py-1 bg-surface-600 text-text-secondary rounded-full">
                        {char}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Subchannels */}
                <div className="p-4">
                  <h4 className="text-xs font-medium text-text-tertiary uppercase tracking-wider mb-1">
                    {t ? 'Desglose por Subcanal' : 'Subchannel Breakdown'}
                  </h4>
                  <p className="text-xs text-text-tertiary mb-3">
                    {t ? '% del valor total del mercado' : '% of total market value'}
                  </p>
                  <div className="space-y-3">
                    {channel.subchannels.map((sub) => (
                      <div key={sub.id} className="flex items-center gap-3">
                        <div className="flex-1">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-sm text-white">{t ? sub.name.es : sub.name.en}</span>
                            <span className="text-sm font-medium text-text-secondary" title={t ? 'Participación en valor' : 'Value share'}>
                              {(sub.share * 100).toFixed(0)}% {t ? 'valor' : 'value'}
                            </span>
                          </div>
                          <div className="h-2 bg-surface-600 rounded-full overflow-hidden">
                            <div
                              className={`h-full rounded-full ${colorMap[channel.color]}`}
                              style={{ width: `${Math.min(sub.share * 100 * 2, 100)}%` }}
                            />
                          </div>
                          <div className="mt-1 text-xs text-text-tertiary">
                            {sub.examples.slice(0, 3).join(' • ')}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      })}

      {/* Channel Share Bar */}
      <div className="card p-5">
        <h3 className="text-sm font-medium text-text-secondary uppercase tracking-wider mb-4">
          {t ? 'Distribución de Ventas por Canal' : 'Sales Distribution by Channel'}
        </h3>
        <div className="h-6 bg-surface-600 rounded-full overflow-hidden flex">
          {channels.map((ch) => (
            <div
              key={ch.id}
              className={`h-full ${colorMap[ch.color]}`}
              style={{ width: `${ch.share * 100}%` }}
              title={`${ch.name}: ${(ch.share * 100).toFixed(0)}%`}
            />
          ))}
        </div>
        <div className="flex flex-wrap justify-between mt-3 gap-2 text-xs">
          {channels.map((ch) => (
            <div key={ch.id} className="flex items-center gap-1.5">
              <span className={`w-2.5 h-2.5 rounded-full ${colorMap[ch.color]}`} />
              <span className="text-text-secondary">{ch.name}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Insight */}
      <InsightCard
        text={t
          ? `${fastestGrowing.name} crece más rápido (+${(fastestGrowing.growth * 100).toFixed(0)}% anual). Considera expandir presencia en subcanales de alto crecimiento.`
          : `${fastestGrowing.name} is growing fastest (+${(fastestGrowing.growth * 100).toFixed(0)}% annually). Consider expanding presence in high-growth subchannels.`}
        type="info"
      />
    </div>
  );
}

// ============================================================================
// COMPETITION SECTION - Rankings and Dynamics
// ============================================================================

// Competitor strategy profiles (mock data - in production would come from API)
const COMPETITOR_PROFILES: Record<string, {
  strategy: { en: string; es: string };
  strengths: { en: string[]; es: string[] };
  recentMoves: { en: string[]; es: string[] };
  threat: 'high' | 'medium' | 'low';
}> = {
  default_leader: {
    strategy: { en: 'Market dominance through scale and brand power', es: 'Dominancia a través de escala y poder de marca' },
    strengths: { en: ['Strong brand equity', 'Wide distribution', 'Marketing spend'], es: ['Fuerte equity de marca', 'Amplia distribución', 'Inversión en marketing'] },
    recentMoves: { en: ['Launched premium line', 'Increased promo intensity', 'E-commerce expansion'], es: ['Lanzó línea premium', 'Aumentó promociones', 'Expansión e-commerce'] },
    threat: 'high',
  },
  default_challenger: {
    strategy: { en: 'Aggressive growth through innovation and pricing', es: 'Crecimiento agresivo a través de innovación y precio' },
    strengths: { en: ['Fast innovation', 'Price competitiveness', 'Digital-first'], es: ['Innovación rápida', 'Competitividad en precio', 'Digital-first'] },
    recentMoves: { en: ['Price war tactics', 'New product launches', 'Social media push'], es: ['Tácticas de guerra de precios', 'Nuevos lanzamientos', 'Push en redes sociales'] },
    threat: 'high',
  },
  default_niche: {
    strategy: { en: 'Differentiation through quality and targeting', es: 'Diferenciación a través de calidad y targeting' },
    strengths: { en: ['Premium positioning', 'Loyal customer base', 'Niche expertise'], es: ['Posicionamiento premium', 'Base de clientes leal', 'Experticia en nicho'] },
    recentMoves: { en: ['Organic certification', 'DTC channel launch', 'Sustainability focus'], es: ['Certificación orgánica', 'Canal DTC', 'Foco en sustentabilidad'] },
    threat: 'medium',
  },
};

function CompetitionSection({ clientId, language }: { clientId: string; language: string }) {
  const { data, isLoading } = useMarketOverview(clientId);
  const t = language === 'es';
  const [expandedBrand, setExpandedBrand] = useState<string | null>(null);

  if (isLoading) return <LoadingSpinner size="md" />;
  if (!data) return null;

  const topBrands = data.top_brands?.slice(0, 8) || [];
  const leader = topBrands[0];

  // Brand colors for logo placeholders (based on real brand colors)
  const brandColors: Record<string, string> = {
    'Coca-Cola': 'bg-red-600',
    'Pepsi': 'bg-blue-600',
    'Dr Pepper': 'bg-amber-800',
    'Sprite': 'bg-green-500',
    'Fanta': 'bg-orange-500',
    'Mountain Dew': 'bg-lime-500',
    'Gatorade': 'bg-orange-400',
    '7-Up': 'bg-green-600',
    'Red Bull': 'bg-yellow-500',
    'Monster': 'bg-green-400',
    'Big Cola': 'bg-red-500',
    'Jarritos': 'bg-amber-500',
    'Aga': 'bg-purple-600',
    'default': 'bg-primary-600',
  };

  // Get initials for brand logo placeholder
  const getBrandInitials = (name: string) => {
    return name
      .split(/[\s-]+/)
      .map(word => word[0])
      .slice(0, 2)
      .join('')
      .toUpperCase();
  };

  const getBrandColor = (name: string) => {
    if (brandColors[name]) return brandColors[name];
    for (const [key, color] of Object.entries(brandColors)) {
      if (name.toLowerCase().includes(key.toLowerCase())) return color;
    }
    return brandColors['default'];
  };

  // Max share for calculating bar widths
  const maxShare = Math.max(...topBrands.map(b => b.share));

  const getProfile = (idx: number) => {
    if (idx === 0) return COMPETITOR_PROFILES.default_leader;
    if (idx < 3) return COMPETITOR_PROFILES.default_challenger;
    return COMPETITOR_PROFILES.default_niche;
  };

  const threatColors = {
    high: 'bg-danger-500/20 text-danger-400 border-danger-500/30',
    medium: 'bg-warning-500/20 text-warning-400 border-warning-500/30',
    low: 'bg-success-500/20 text-success-400 border-success-500/30',
  };

  return (
    <div className="space-y-6">
      {/* Market Concentration Summary */}
      <div className="grid grid-cols-3 gap-4">
        <div className="card p-4 text-center">
          <div className="text-xs text-text-tertiary uppercase tracking-wider mb-1" title={t ? 'Participación en valor' : 'Value share'}>{t ? 'Líder (Valor)' : 'Leader (Value)'}</div>
          <div className="text-2xl font-bold text-white">{((leader?.share || 0) * 100).toFixed(0)}%</div>
        </div>
        <div className="card p-4 text-center">
          <div className="text-xs text-text-tertiary uppercase tracking-wider mb-1" title={t ? 'Participación en valor combinada' : 'Combined value share'}>{t ? 'Top 3 (Valor)' : 'Top 3 (Value)'}</div>
          <div className="text-2xl font-bold text-primary-400">
            {((topBrands.slice(0, 3).reduce((s, b) => s + b.share, 0)) * 100).toFixed(0)}%
          </div>
        </div>
        <div className="card p-4 text-center">
          <div className="text-xs text-text-tertiary uppercase tracking-wider mb-1" title={t ? 'Participación en valor combinada' : 'Combined value share'}>{t ? 'Top 5 (Valor)' : 'Top 5 (Value)'}</div>
          <div className="text-2xl font-bold text-amber-400">
            {((topBrands.slice(0, 5).reduce((s, b) => s + b.share, 0)) * 100).toFixed(0)}%
          </div>
        </div>
      </div>

      {/* Player Deep Dive List */}
      <div className="card p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-medium text-text-secondary uppercase tracking-wider">
            {t ? 'Jugadores del Mercado' : 'Market Players'}
          </h3>
          <span className="text-xs text-text-tertiary px-2 py-1 bg-surface-600/50 rounded" title={t ? 'Las barras muestran participación en valor del mercado' : 'Bars show value share of market'}>
            {t ? '📊 Valor' : '📊 Value Share'}
          </span>
        </div>
        <div className="sr-only">{t ? 'Participación de mercado en valor (no en volumen)' : 'Market share by value (not volume)'}</div>
        <div className="space-y-2">
          {topBrands.map((brand, idx) => {
            const isExpanded = expandedBrand === brand.name;
            const profile = getProfile(idx);
            const brandColor = getBrandColor(brand.name);
            const initials = getBrandInitials(brand.name);
            const growth = brand.growth !== undefined ? (brand.growth * 100).toFixed(1) : null;
            const shareBarWidth = maxShare > 0 ? (brand.share / maxShare) * 100 : 0;

            return (
              <div key={brand.name} className="border border-surface-600 rounded-lg overflow-hidden">
                <button
                  onClick={() => setExpandedBrand(isExpanded ? null : brand.name)}
                  className="w-full p-3 text-left hover:bg-surface-600/30 transition-colors"
                >
                  <div className="flex items-center gap-4">
                    {/* Logo Placeholder with Initials */}
                    <span className={`w-10 h-10 rounded-lg flex items-center justify-center text-sm font-bold text-white shrink-0 ${
                      idx === 0 ? 'ring-2 ring-warning-400/50' : ''
                    } ${brandColor}`}>
                      {initials}
                    </span>

                    {/* Brand Info + Share Bar */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="font-medium text-white truncate">{brand.name}</span>
                          {idx === 0 && <span className="text-warning-400 text-sm shrink-0">🏆</span>}
                          {brand.manufacturer && (
                            <span className="text-xs text-text-tertiary hidden sm:inline">• {brand.manufacturer}</span>
                          )}
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <span className="text-sm font-semibold text-primary-400">{(brand.share * 100).toFixed(1)}%</span>
                          {growth && (
                            <span className={`text-xs ${Number(growth) >= 0 ? 'text-success-400' : 'text-danger-400'}`}>
                              {Number(growth) >= 0 ? '+' : ''}{growth}%
                            </span>
                          )}
                        </div>
                      </div>
                      {/* Share Bar */}
                      <div className="h-2 bg-surface-600 rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full transition-all ${idx === 0 ? 'bg-warning-500' : idx < 3 ? 'bg-primary-500' : 'bg-primary-400/60'}`}
                          style={{ width: `${shareBarWidth}%` }}
                        />
                      </div>
                    </div>

                    {/* Threat Badge + Expand Arrow */}
                    <div className="flex items-center gap-2 shrink-0">
                      <span className={`text-xs px-2 py-1 rounded border hidden sm:inline ${threatColors[profile.threat]}`}>
                        {profile.threat === 'high' ? (t ? 'Alta' : 'High') : profile.threat === 'medium' ? (t ? 'Media' : 'Med') : (t ? 'Baja' : 'Low')}
                      </span>
                      <svg className={`w-4 h-4 text-text-secondary transition-transform ${isExpanded ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  </div>
                </button>

                {isExpanded && (
                  <div className="border-t border-surface-600 p-4 bg-surface-800/50 space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      {/* Strategy */}
                      <div>
                        <h5 className="text-xs font-medium text-text-tertiary uppercase tracking-wider mb-2">
                          {t ? 'Estrategia' : 'Strategy'}
                        </h5>
                        <p className="text-sm text-white">
                          {t ? profile.strategy.es : profile.strategy.en}
                        </p>
                      </div>

                      {/* Strengths */}
                      <div>
                        <h5 className="text-xs font-medium text-text-tertiary uppercase tracking-wider mb-2">
                          {t ? 'Fortalezas' : 'Key Strengths'}
                        </h5>
                        <div className="flex flex-wrap gap-1.5">
                          {(t ? profile.strengths.es : profile.strengths.en).slice(0, 3).map((str, i) => (
                            <span key={i} className="text-xs px-2 py-1 bg-success-500/10 text-success-400 rounded">
                              {str}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* Recent Moves */}
                    <div>
                      <h5 className="text-xs font-medium text-text-tertiary uppercase tracking-wider mb-2">
                        {t ? 'Movimientos Recientes' : 'Recent Moves'}
                      </h5>
                      <ul className="space-y-1">
                        {(t ? profile.recentMoves.es : profile.recentMoves.en).slice(0, 3).map((move, i) => (
                          <li key={i} className="text-sm text-text-secondary flex items-center gap-2">
                            <span className="w-1.5 h-1.5 bg-primary-500 rounded-full flex-shrink-0" />
                            {move}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Insight */}
      {leader && (
        <InsightCard
          text={t
            ? `${leader.name} lidera con ${(leader.share * 100).toFixed(0)}% del mercado. Los top 3 controlan ${((topBrands.slice(0, 3).reduce((sum, b) => sum + b.share, 0)) * 100).toFixed(0)}%.`
            : `${leader.name} leads with ${(leader.share * 100).toFixed(0)}% market share. Top 3 control ${((topBrands.slice(0, 3).reduce((sum, b) => sum + b.share, 0)) * 100).toFixed(0)}%.`}
          type="info"
          sources={[
            { name: 'Nielsen Value Share', type: 'nielsen', date: '2025 Q4', confidence: 0.92 }
          ]}
          language={language}
        />
      )}
    </div>
  );
}

// ============================================================================
// COMPANY SECTION - Position and Portfolio sub-sections
// ============================================================================
function CompanySection({ clientId, language, subSection }: { clientId: string; language: string; subSection: string }) {
  const { data: position, isLoading: posLoading } = usePortfolioPosition(clientId);
  const { data: health, isLoading: healthLoading } = useQuery({
    queryKey: ['client', clientId, 'portfolio', 'health'],
    queryFn: () => apiClient.portfolio.getHealth(clientId),
    enabled: !!clientId,
  });
  const t = language === 'es';

  if (posLoading || healthLoading) return <LoadingSpinner size="md" />;
  if (!position) return null;

  // Render Portfolio sub-section
  if (subSection === 'portfolio') {
    return <CompanyPortfolioView position={position} health={health} language={language} />;
  }

  // Render Costs sub-section
  if (subSection === 'costs') {
    return <CostStructureView clientId={clientId} language={language} />;
  }

  // Default: Position view
  return <CompanyPositionView position={position} health={health} language={language} />;
}

// Company Position View - Market standing and competitive position
function CompanyPositionView({ position, health, language }: { position: any; health: any; language: string }) {
  const t = language === 'es';

  const rank = position.rank || 0;
  const share = (position.total_share || 0) * 100;
  const gapToLeader = (position.gap_to_leader || 0) * 100;
  const skuCount = health?.total_skus || health?.sku_count || 0;
  const positionStatus = rank <= 2 ? 'leader' : rank <= 5 ? 'challenger' : 'niche';

  // SWOT data from position (with fallbacks for demo)
  const strengths = position.strengths || [
    t ? 'Líder de mercado - ventajas de escala y espacio en anaquel' : 'Market leader - scale advantages and shelf space leverage',
    t ? 'Portafolio diversificado - múltiples puntos de precio y segmentos' : 'Diversified portfolio - multiple price points and segments',
    t ? 'Fuerte equity de marca - >30% share indica preferencia del consumidor' : 'Strong brand equity - >30% share indicates consumer preference',
  ];
  const weaknesses = position.weaknesses || [
    t ? 'Alta dependencia de canal tradicional (65% de volumen)' : 'High dependence on traditional channel (65% of volume)',
    t ? 'Margen menor en segmento económico vs competidores' : 'Lower margin in economy segment vs competitors',
    t ? 'Baja penetración en e-commerce (<3% de ventas)' : 'Low e-commerce penetration (<3% of sales)',
  ];
  const opportunities = position.opportunities || [
    t ? 'Segmento premium en crecimiento - baja sensibilidad al precio' : 'Premium segment opportunity - low price sensitivity',
    t ? 'Expansión en canal conveniencia (+8% YoY)' : 'Convenience channel expansion (+8% YoY)',
    t ? 'Innovación en formatos saludables/funcionales' : 'Innovation in healthy/functional formats',
  ];
  const threats = position.threats || [
    t ? 'Entrada agresiva de marcas privadas en segmento económico' : 'Aggressive private label entry in economy segment',
    t ? 'Competidor #2 aumentando inversión en marketing digital' : 'Competitor #2 increasing digital marketing investment',
    t ? 'Regulaciones de impuestos a bebidas azucaradas en discusión' : 'Sugar beverage tax regulations under discussion',
  ];
  const hasSWOT = strengths.length > 0 || weaknesses.length > 0;

  return (
    <div className="space-y-6">
      {/* Position KPIs */}
      <div className="grid grid-cols-4 gap-4">
        <div className="card p-5 text-center">
          <div className="text-xs text-text-tertiary uppercase tracking-wider mb-2">
            {t ? 'Posición' : 'Market Rank'}
          </div>
          <div className="text-3xl font-bold text-primary-400">#{rank}</div>
          <div className="text-xs text-text-tertiary mt-1">{t ? 'en el mercado' : 'in market'}</div>
        </div>
        <div className="card p-5 text-center">
          <div className="text-xs text-text-tertiary uppercase tracking-wider mb-2" title={t ? 'Participación en valor del mercado' : 'Value share of market'}>
            {t ? 'Participación (Valor)' : 'Value Share'}
          </div>
          <div className="text-3xl font-bold text-white">{share.toFixed(1)}%</div>
          <div className="text-xs text-text-tertiary mt-1">{t ? 'del mercado' : 'of market'}</div>
        </div>
        <div className="card p-5 text-center">
          <div className="text-xs text-text-tertiary uppercase tracking-wider mb-2">
            {t ? 'Gap al Líder' : 'Gap to Leader'}
          </div>
          <div className={`text-3xl font-bold ${gapToLeader > 0 ? 'text-warning-400' : 'text-success-400'}`}>
            {gapToLeader > 0 ? `${gapToLeader.toFixed(1)}pp` : '—'}
          </div>
          <div className="text-xs text-text-tertiary mt-1">{t ? 'puntos porcentuales' : 'percentage points'}</div>
        </div>
        <div className="card p-5 text-center">
          <div className="text-xs text-text-tertiary uppercase tracking-wider mb-2">SKUs</div>
          <div className="text-3xl font-bold text-white">{skuCount}</div>
          <div className="text-xs text-text-tertiary mt-1">{t ? 'en portafolio' : 'in portfolio'}</div>
        </div>
      </div>

      {/* Position Status Card */}
      <div className={`card p-5 border-l-4 ${
        positionStatus === 'leader' ? 'border-l-success-500 bg-success-500/5' :
        positionStatus === 'challenger' ? 'border-l-warning-500 bg-warning-500/5' :
        'border-l-primary-500 bg-primary-500/5'
      }`}>
        <div className="flex items-center gap-3 mb-2">
          <span className="text-2xl">
            {positionStatus === 'leader' ? '🏆' : positionStatus === 'challenger' ? '⚔️' : '🎯'}
          </span>
          <div>
            <h3 className="font-semibold text-white">
              {positionStatus === 'leader'
                ? (t ? 'Líder del Mercado' : 'Market Leader')
                : positionStatus === 'challenger'
                  ? (t ? 'Retador' : 'Challenger')
                  : (t ? 'Jugador de Nicho' : 'Niche Player')}
            </h3>
            <p className="text-sm text-text-secondary">
              {positionStatus === 'leader'
                ? (t ? 'Enfócate en defender posición y expandir márgenes' : 'Focus on defending position and expanding margins')
                : positionStatus === 'challenger'
                  ? (t ? 'Agresividad y diferenciación para ganar share' : 'Aggression and differentiation to gain share')
                  : (t ? 'Especialización y diferenciación clara' : 'Specialization and clear differentiation')}
            </p>
          </div>
        </div>
      </div>

      {/* SWOT Analysis */}
      {hasSWOT && (
        <div className="card p-5">
          <h3 className="text-sm font-medium text-text-secondary uppercase tracking-wider mb-4">
            {t ? 'Análisis FODA' : 'SWOT Analysis'}
          </h3>
          <div className="grid grid-cols-2 gap-4">
            <div className="p-3 bg-success-500/10 rounded-lg border border-success-500/20">
              <h4 className="text-xs font-medium text-success-400 uppercase mb-2">{t ? 'Fortalezas' : 'Strengths'}</h4>
              {strengths.length > 0 ? (
                <ul className="space-y-1">
                  {strengths.slice(0, 3).map((s: string, i: number) => (
                    <li key={i} className="text-sm text-text-secondary flex items-start gap-2">
                      <span className="text-success-400">✓</span> {s}
                    </li>
                  ))}
                </ul>
              ) : <p className="text-sm text-text-tertiary">—</p>}
            </div>
            <div className="p-3 bg-danger-500/10 rounded-lg border border-danger-500/20">
              <h4 className="text-xs font-medium text-danger-400 uppercase mb-2">{t ? 'Debilidades' : 'Weaknesses'}</h4>
              {weaknesses.length > 0 ? (
                <ul className="space-y-1">
                  {weaknesses.slice(0, 3).map((w: string, i: number) => (
                    <li key={i} className="text-sm text-text-secondary flex items-start gap-2">
                      <span className="text-danger-400">✗</span> {w}
                    </li>
                  ))}
                </ul>
              ) : <p className="text-sm text-text-tertiary">—</p>}
            </div>
            <div className="p-3 bg-primary-500/10 rounded-lg border border-primary-500/20">
              <h4 className="text-xs font-medium text-primary-400 uppercase mb-2">{t ? 'Oportunidades' : 'Opportunities'}</h4>
              {opportunities.length > 0 ? (
                <ul className="space-y-1">
                  {opportunities.slice(0, 3).map((o: string, i: number) => (
                    <li key={i} className="text-sm text-text-secondary flex items-start gap-2">
                      <span className="text-primary-400">→</span> {o}
                    </li>
                  ))}
                </ul>
              ) : <p className="text-sm text-text-tertiary">—</p>}
            </div>
            <div className="p-3 bg-warning-500/10 rounded-lg border border-warning-500/20">
              <h4 className="text-xs font-medium text-warning-400 uppercase mb-2">{t ? 'Amenazas' : 'Threats'}</h4>
              {threats.length > 0 ? (
                <ul className="space-y-1">
                  {threats.slice(0, 3).map((th: string, i: number) => (
                    <li key={i} className="text-sm text-text-secondary flex items-start gap-2">
                      <span className="text-warning-400">⚠</span> {th}
                    </li>
                  ))}
                </ul>
              ) : <p className="text-sm text-text-tertiary">—</p>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Company Portfolio View - Portfolio health and architecture
function CompanyPortfolioView({ position, health, language }: { position: any; health: any; language: string }) {
  const t = language === 'es';

  const skuCount = health?.total_skus || health?.sku_count || 0;
  const concentration = health?.concentration_index || health?.hhi || 0;
  const overlap = health?.overlap_score || 0;
  const healthScore = health?.health_score || (1 - overlap);
  const recommendations = health?.recommendations || [];

  // Interpret concentration
  const concentrationLevel = concentration > 0.25 ? 'high' : concentration > 0.15 ? 'medium' : 'low';
  const healthLevel = healthScore > 0.7 ? 'good' : healthScore > 0.5 ? 'moderate' : 'poor';

  return (
    <div className="space-y-6">
      {/* Portfolio Health KPIs */}
      <div className="grid grid-cols-3 gap-4">
        <div className="card p-5 text-center">
          <div className="text-xs text-text-tertiary uppercase tracking-wider mb-2">
            {t ? 'Total SKUs' : 'Total SKUs'}
          </div>
          <div className="text-3xl font-bold text-white">{skuCount}</div>
          <div className="text-xs text-text-tertiary mt-1">{t ? 'productos activos' : 'active products'}</div>
        </div>
        <div className="card p-5 text-center">
          <div className="text-xs text-text-tertiary uppercase tracking-wider mb-2">
            {t ? 'Concentración' : 'Concentration'}
          </div>
          <div className={`text-3xl font-bold ${
            concentrationLevel === 'high' ? 'text-danger-400' : concentrationLevel === 'medium' ? 'text-warning-400' : 'text-success-400'
          }`}>
            {(concentration * 100).toFixed(0)}%
          </div>
          <div className="text-xs text-text-tertiary mt-1">HHI {t ? 'index' : 'index'}</div>
        </div>
        <div className="card p-5 text-center">
          <div className="text-xs text-text-tertiary uppercase tracking-wider mb-2">
            {t ? 'Salud del Portafolio' : 'Portfolio Health'}
          </div>
          <div className={`text-3xl font-bold ${
            healthLevel === 'good' ? 'text-success-400' : healthLevel === 'moderate' ? 'text-warning-400' : 'text-danger-400'
          }`}>
            {(healthScore * 100).toFixed(0)}%
          </div>
          <div className="text-xs text-text-tertiary mt-1">
            {t ? '100% − canibalización' : '100% − cannibalization'}
          </div>
        </div>
      </div>

      {/* Portfolio Metrics Detail */}
      <div className="card p-5">
        <h3 className="text-sm font-medium text-text-secondary uppercase tracking-wider mb-4">
          {t ? 'Métricas del Portafolio' : 'Portfolio Metrics'}
        </h3>
        <div className="space-y-4">
          {/* Concentration Bar */}
          <div>
            <div className="flex justify-between items-center mb-1">
              <span className="text-sm text-text-secondary">{t ? 'Concentración de Ventas (HHI)' : 'Sales Concentration (HHI)'}</span>
              <span className={`text-sm font-medium ${
                concentrationLevel === 'high' ? 'text-danger-400' : concentrationLevel === 'medium' ? 'text-warning-400' : 'text-success-400'
              }`}>
                {concentrationLevel === 'high' ? (t ? 'Alta' : 'High') : concentrationLevel === 'medium' ? (t ? 'Media' : 'Medium') : (t ? 'Baja' : 'Low')}
              </span>
            </div>
            <div className="h-3 bg-surface-600 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full ${
                  concentrationLevel === 'high' ? 'bg-danger-500' : concentrationLevel === 'medium' ? 'bg-warning-500' : 'bg-success-500'
                }`}
                style={{ width: `${Math.min(concentration * 100 * 2, 100)}%` }}
              />
            </div>
            <p className="text-xs text-text-tertiary mt-1">
              {t ? 'Qué tan dependiente eres de pocos SKUs. Menor = mejor diversificación.' : 'How dependent you are on few SKUs. Lower = better diversification.'}
            </p>
          </div>

          {/* Overlap/Cannibalization */}
          <div>
            <div className="flex justify-between items-center mb-1">
              <span className="text-sm text-text-secondary">{t ? 'Solapamiento / Canibalización' : 'Overlap / Cannibalization'}</span>
              <span className={`text-sm font-medium ${overlap > 0.3 ? 'text-danger-400' : overlap > 0.15 ? 'text-warning-400' : 'text-success-400'}`}>
                {(overlap * 100).toFixed(0)}%
              </span>
            </div>
            <div className="h-3 bg-surface-600 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full ${overlap > 0.3 ? 'bg-danger-500' : overlap > 0.15 ? 'bg-warning-500' : 'bg-success-500'}`}
                style={{ width: `${Math.min(overlap * 100 * 2, 100)}%` }}
              />
            </div>
            <p className="text-xs text-text-tertiary mt-1">
              {t ? 'Qué tanto compiten tus productos entre sí. Menor = menos canibalización.' : 'How much your products compete with each other. Lower = less cannibalization.'}
            </p>
          </div>

          {/* Health Score Explanation */}
          <div className="pt-4 border-t border-surface-600">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm text-text-secondary">{t ? 'Salud del Portafolio' : 'Portfolio Health'}</span>
              <span className={`text-sm font-medium ${
                healthLevel === 'good' ? 'text-success-400' : healthLevel === 'moderate' ? 'text-warning-400' : 'text-danger-400'
              }`}>
                {(healthScore * 100).toFixed(0)}%
              </span>
            </div>
            <div className="bg-surface-700/50 rounded-lg p-3">
              <p className="text-xs text-text-tertiary">
                <span className="font-mono text-primary-400">{t ? 'Fórmula' : 'Formula'}:</span> 100% − {t ? 'Canibalización' : 'Cannibalization'} = {(healthScore * 100).toFixed(0)}%
              </p>
              <p className="text-xs text-text-tertiary mt-2">
                {t
                  ? 'Mide qué tan eficiente es tu portafolio. Un score alto significa que tus productos no compiten entre sí y cada uno aporta valor único al mercado.'
                  : 'Measures how efficient your portfolio is. A high score means your products don\'t compete with each other and each provides unique market value.'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Recommendations */}
      {recommendations.length > 0 && (
        <div className="card p-5">
          <h3 className="text-sm font-medium text-text-secondary uppercase tracking-wider mb-4">
            {t ? 'Recomendaciones' : 'Recommendations'}
          </h3>
          <div className="space-y-2">
            {recommendations.slice(0, 4).map((rec: string, i: number) => (
              <div key={i} className="flex items-start gap-3 p-3 bg-surface-700/50 rounded-lg">
                <span className="text-primary-400 mt-0.5">💡</span>
                <p className="text-sm text-text-secondary">{rec}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Insight */}
      <InsightCard
        text={
          concentrationLevel === 'high'
            ? (t ? `Alta concentración (${(concentration * 100).toFixed(0)}%). Diversificar el portafolio reduciría riesgo.` : `High concentration (${(concentration * 100).toFixed(0)}%). Diversifying portfolio would reduce risk.`)
            : overlap > 0.2
              ? (t ? `Solapamiento de ${(overlap * 100).toFixed(0)}% sugiere oportunidad de racionalización.` : `Overlap of ${(overlap * 100).toFixed(0)}% suggests rationalization opportunity.`)
              : (t ? 'Portafolio bien balanceado con buena diversificación.' : 'Well-balanced portfolio with good diversification.')
        }
        type={concentrationLevel === 'high' || overlap > 0.2 ? 'warning' : 'success'}
      />
    </div>
  );
}

// ============================================================================
// SEASONALITY VIEW - Monthly sales pattern (FEMSA differentiated)
// ============================================================================
function SeasonalityView({ clientId, language }: { clientId: string; language: string }) {
  const { data, isLoading } = useMarketSeasonality(clientId);
  const t = language === 'es';

  if (isLoading) return <LoadingSpinner size="md" />;
  if (!data) return null;

  const isProprietary = data.has_data && data.source === 'proprietary';
  const indices = data.data.monthly_indices || [];
  const summary = data.data.summary;

  // Find max index for scaling
  const maxIndex = Math.max(...indices.map((m: any) => m.index));
  const minIndex = Math.min(...indices.map((m: any) => m.index));

  return (
    <div className="space-y-6">
      {/* Data Source Badge */}
      <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium ${
        isProprietary
          ? 'bg-success-500/20 text-success-400 border border-success-500/30'
          : 'bg-surface-600 text-text-tertiary border border-surface-500'
      }`}>
        {isProprietary ? '🥇' : '📊'} {isProprietary ? (t ? 'Datos Propietarios (Nielsen)' : 'Proprietary Data (Nielsen)') : (t ? 'Estimados de Línea Base' : 'Baseline Estimates')}
      </div>

      {/* Monthly Indices Chart */}
      <div className="card p-5">
        <h3 className="text-sm font-medium text-text-secondary uppercase tracking-wider mb-4">
          {t ? 'Índices Mensuales de Estacionalidad' : 'Monthly Seasonality Indices'}
        </h3>
        <div className="flex items-end gap-1 h-48">
          {indices.map((month: any, i: number) => {
            const height = ((month.index - minIndex + 20) / (maxIndex - minIndex + 40)) * 100;
            const isPeak = month.index >= 115;
            const isTrough = month.index <= 85;
            return (
              <div key={i} className="flex-1 flex flex-col items-center">
                <div className="text-xs text-text-tertiary mb-1">{month.index.toFixed(0)}</div>
                <div
                  className={`w-full rounded-t transition-all ${
                    isPeak ? 'bg-success-500' : isTrough ? 'bg-danger-500' : 'bg-primary-500/60'
                  }`}
                  style={{ height: `${height}%` }}
                  title={month.notes || ''}
                />
                <div className="text-xs text-text-tertiary mt-2 truncate w-full text-center">
                  {month.name?.substring(0, 3) || `M${month.month}`}
                </div>
              </div>
            );
          })}
        </div>
        {/* Legend */}
        <div className="flex items-center gap-4 mt-4 pt-4 border-t border-surface-700 text-xs text-text-tertiary">
          <span className="flex items-center gap-1"><span className="w-3 h-3 bg-success-500 rounded" /> {t ? 'Pico (>115)' : 'Peak (>115)'}</span>
          <span className="flex items-center gap-1"><span className="w-3 h-3 bg-danger-500 rounded" /> {t ? 'Valle (<85)' : 'Trough (<85)'}</span>
          <span className="flex items-center gap-1"><span className="w-3 h-3 bg-primary-500/60 rounded" /> {t ? 'Normal' : 'Normal'}</span>
        </div>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-3 gap-4">
          <div className="card p-5 text-center border-l-4 border-success-500">
            <div className="text-xs text-text-tertiary uppercase tracking-wider mb-2">
              {t ? 'Mes Pico' : 'Peak Month'}
            </div>
            <div className="text-2xl font-bold text-success-400">{summary.highest_month.month}</div>
            <div className="text-sm text-text-secondary mt-1">Index: {summary.highest_month.index}</div>
            {summary.highest_month.driver && (
              <div className="text-xs text-text-tertiary mt-2">{summary.highest_month.driver}</div>
            )}
          </div>
          <div className="card p-5 text-center border-l-4 border-danger-500">
            <div className="text-xs text-text-tertiary uppercase tracking-wider mb-2">
              {t ? 'Mes Bajo' : 'Low Month'}
            </div>
            <div className="text-2xl font-bold text-danger-400">{summary.lowest_month.month}</div>
            <div className="text-sm text-text-secondary mt-1">Index: {summary.lowest_month.index}</div>
            {summary.lowest_month.driver && (
              <div className="text-xs text-text-tertiary mt-2">{summary.lowest_month.driver}</div>
            )}
          </div>
          <div className="card p-5 text-center">
            <div className="text-xs text-text-tertiary uppercase tracking-wider mb-2">
              {t ? 'Rango' : 'Range'}
            </div>
            <div className="text-2xl font-bold text-white">{summary.range.spread.toFixed(1)}</div>
            <div className="text-sm text-text-secondary mt-1">{summary.range.low} → {summary.range.high}</div>
            <div className="text-xs text-text-tertiary mt-2">{t ? 'puntos de variación' : 'index points'}</div>
          </div>
        </div>
      )}

      {/* Holiday Effects (if proprietary) */}
      {isProprietary && data.data.holiday_effects && (
        <div className="card p-5">
          <h3 className="text-sm font-medium text-text-secondary uppercase tracking-wider mb-4">
            {t ? 'Efectos de Fiestas' : 'Holiday Effects'}
          </h3>
          <div className="grid grid-cols-2 gap-3">
            {data.data.holiday_effects.slice(0, 4).map((holiday: any, i: number) => (
              <div key={i} className="p-3 bg-surface-700/50 rounded-lg">
                <div className="flex items-center justify-between">
                  <span className="font-medium text-white text-sm">{holiday.event}</span>
                  <span className="text-success-400 text-sm">+{holiday.effect_pct}%</span>
                </div>
                <div className="text-xs text-text-tertiary mt-1">{holiday.timing}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Insight */}
      <InsightCard
        text={isProprietary
          ? (t
            ? `Datos de ${data.data.metadata?.months_of_data || 36} meses muestran un rango de ${summary?.range.spread.toFixed(0)} puntos. ${summary?.highest_month.month} es el pico (+${((summary?.highest_month.index || 100) - 100).toFixed(0)}%).`
            : `${data.data.metadata?.months_of_data || 36} months of data show a ${summary?.range.spread.toFixed(0)}-point range. ${summary?.highest_month.month} is peak (+${((summary?.highest_month.index || 100) - 100).toFixed(0)}%).`)
          : (t ? 'Usando estimaciones de línea base. Datos propietarios mejorarían la precisión.' : 'Using baseline estimates. Proprietary data would improve accuracy.')}
        type={isProprietary ? 'success' : 'info'}
      />
    </div>
  );
}

// ============================================================================
// COST STRUCTURE VIEW - P&L and margins (FEMSA differentiated)
// ============================================================================
function CostStructureView({ clientId, language }: { clientId: string; language: string }) {
  const { data, isLoading } = useMarketCosts(clientId);
  const t = language === 'es';

  if (isLoading) return <LoadingSpinner size="md" />;
  if (!data) return null;

  const isProprietary = data.has_data && data.source === 'proprietary';
  const metrics = data.data.key_metrics;
  const pnl = data.data.pnl;
  const waterfall = data.data.margin_waterfall;

  return (
    <div className="space-y-6">
      {/* Data Source Badge */}
      <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium ${
        isProprietary
          ? 'bg-success-500/20 text-success-400 border border-success-500/30'
          : 'bg-surface-600 text-text-tertiary border border-surface-500'
      }`}>
        {isProprietary ? '🥇' : '📊'} {isProprietary ? (t ? 'Datos Internos Confidenciales' : 'Confidential Internal Data') : (t ? 'Estimados de Industria' : 'Industry Estimates')}
      </div>

      {/* Key Metrics Cards */}
      <div className="grid grid-cols-3 gap-4">
        <div className="card p-5 text-center">
          <div className="text-xs text-text-tertiary uppercase tracking-wider mb-2">
            {t ? 'Margen Bruto' : 'Gross Margin'}
          </div>
          <div className="text-3xl font-bold text-success-400">{metrics.gross_margin.actual}%</div>
          {metrics.gross_margin.previous_estimate && (
            <div className="text-xs text-text-tertiary mt-1">
              {t ? 'vs' : 'vs'} {metrics.gross_margin.previous_estimate}% {t ? 'estimado industria' : 'industry estimate'}
            </div>
          )}
        </div>
        {metrics.operating_margin && (
          <div className="card p-5 text-center">
            <div className="text-xs text-text-tertiary uppercase tracking-wider mb-2">
              {t ? 'Margen Operativo' : 'Operating Margin'}
            </div>
            <div className={`text-3xl font-bold ${metrics.operating_margin.actual > 5 ? 'text-success-400' : 'text-warning-400'}`}>
              {metrics.operating_margin.actual}%
            </div>
            {metrics.operating_margin.notes && (
              <div className="text-xs text-text-tertiary mt-1">{metrics.operating_margin.notes}</div>
            )}
          </div>
        )}
        {metrics.cogs_ratio && (
          <div className="card p-5 text-center">
            <div className="text-xs text-text-tertiary uppercase tracking-wider mb-2">
              {t ? 'Ratio COGS' : 'COGS Ratio'}
            </div>
            <div className="text-3xl font-bold text-white">{metrics.cogs_ratio.actual}%</div>
            {metrics.cogs_ratio.notes && (
              <div className="text-xs text-text-tertiary mt-1">{metrics.cogs_ratio.notes}</div>
            )}
          </div>
        )}
      </div>

      {/* Margin Waterfall (if proprietary) */}
      {isProprietary && waterfall && (
        <div className="card p-5">
          <h3 className="text-sm font-medium text-text-secondary uppercase tracking-wider mb-4">
            {t ? 'Cascada de Margen' : 'Margin Waterfall'}
          </h3>
          <div className="space-y-2">
            {waterfall.steps.map((step: any, i: number) => {
              const isPositive = step.value_pct > 0;
              const isTotal = step.step.includes('=');
              return (
                <div key={i} className={`flex items-center gap-3 p-2 rounded ${isTotal ? 'bg-surface-600' : 'bg-surface-700/50'}`}>
                  <div className="flex-1 text-sm text-text-secondary">{step.step}</div>
                  <div className={`text-sm font-medium ${
                    isTotal ? 'text-white' : isPositive ? 'text-white' : 'text-danger-400'
                  }`}>
                    {isPositive && !isTotal ? '' : ''}{step.value_pct.toFixed(1)}%
                  </div>
                  {step.cumulative !== undefined && (
                    <div className="text-xs text-text-tertiary w-16 text-right">
                      → {step.cumulative.toFixed(1)}%
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* P&L Summary (if proprietary) */}
      {isProprietary && pnl && (
        <div className="card p-5">
          <h3 className="text-sm font-medium text-text-secondary uppercase tracking-wider mb-4">
            {t ? 'Resumen P&L' : 'P&L Summary'}
          </h3>
          <div className="grid grid-cols-2 gap-4">
            {pnl.net_sales && (
              <div className="p-3 bg-surface-700/50 rounded-lg">
                <div className="text-xs text-text-tertiary">{t ? 'Ventas Netas' : 'Net Sales'}</div>
                <div className="text-lg font-bold text-white">{pnl.net_sales.value_mxn_formatted}</div>
              </div>
            )}
            {pnl.gross_profit && (
              <div className="p-3 bg-surface-700/50 rounded-lg">
                <div className="text-xs text-text-tertiary">{t ? 'Utilidad Bruta' : 'Gross Profit'}</div>
                <div className="text-lg font-bold text-success-400">{pnl.gross_profit.value_mxn_formatted}</div>
              </div>
            )}
            {pnl.cogs && (
              <div className="p-3 bg-surface-700/50 rounded-lg">
                <div className="text-xs text-text-tertiary">COGS</div>
                <div className="text-lg font-bold text-white">{pnl.cogs.value_mxn_formatted}</div>
              </div>
            )}
            {pnl.operating_profit && (
              <div className="p-3 bg-surface-700/50 rounded-lg">
                <div className="text-xs text-text-tertiary">{t ? 'Utilidad Operativa' : 'Operating Profit'}</div>
                <div className="text-lg font-bold text-warning-400">{pnl.operating_profit.value_mxn_formatted}</div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Margin Comparison (Proprietary vs Industry) */}
      {metrics.gross_margin.previous_estimate && (
        <div className="card p-5 bg-primary-500/5 border-primary-500/20">
          <h3 className="text-sm font-medium text-primary-400 mb-4">
            {t ? 'Comparación: Datos Reales vs Estimados de Industria' : 'Comparison: Actual Data vs Industry Estimates'}
          </h3>
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <div className="flex justify-between text-sm mb-1">
                <span className="text-text-secondary">{t ? 'Industria' : 'Industry'}</span>
                <span className="text-text-tertiary">{metrics.gross_margin.previous_estimate}%</span>
              </div>
              <div className="h-4 bg-surface-600 rounded-full overflow-hidden">
                <div className="h-full bg-surface-500 rounded-full" style={{ width: `${metrics.gross_margin.previous_estimate}%` }} />
              </div>
            </div>
            <div className="text-2xl text-text-tertiary">→</div>
            <div className="flex-1">
              <div className="flex justify-between text-sm mb-1">
                <span className="text-text-secondary">{t ? 'Actual' : 'Actual'}</span>
                <span className="text-success-400 font-medium">{metrics.gross_margin.actual}%</span>
              </div>
              <div className="h-4 bg-surface-600 rounded-full overflow-hidden">
                <div className="h-full bg-success-500 rounded-full" style={{ width: `${metrics.gross_margin.actual}%` }} />
              </div>
            </div>
          </div>
          <div className="mt-3 text-sm text-text-secondary">
            {metrics.gross_margin.delta && (
              <span className="text-success-400 font-medium">{metrics.gross_margin.delta}</span>
            )} {t ? 'diferencia vs estimados de industria' : 'difference vs industry estimates'}
          </div>
        </div>
      )}

      {/* Insight */}
      <InsightCard
        text={isProprietary
          ? (t
            ? `Margen bruto real ${metrics.gross_margin.actual}% vs ${metrics.gross_margin.previous_estimate || 44.5}% estimado. Los datos internos revelan operaciones más eficientes que el promedio de la industria.`
            : `Actual gross margin ${metrics.gross_margin.actual}% vs ${metrics.gross_margin.previous_estimate || 44.5}% estimated. Internal data reveals more efficient operations than industry average.`)
          : (t ? 'Usando estimaciones de industria. Datos financieros internos mejorarían la precisión de simulaciones.' : 'Using industry estimates. Internal financial data would improve simulation accuracy.')}
        type={isProprietary ? 'success' : 'info'}
      />
    </div>
  );
}

// ============================================================================
// DATA SOURCES SECTION - Shows data fidelity and recency
// ============================================================================
function DataSourcesSection({ clientId, language }: { clientId: string; language: string }) {
  const t = language === 'es';

  // Fetch quality data from API
  const { data: quality, isLoading } = useQuery({
    queryKey: ['client', clientId, 'quality'],
    queryFn: () => apiClient.quality?.getAssessment?.(clientId).catch(() => null) || Promise.resolve(null),
    enabled: !!clientId,
  });

  if (isLoading) return <LoadingSpinner size="md" />;

  // Mock/default data sources info
  const dataSources = [
    {
      id: 'market',
      name: t ? 'Datos de Mercado' : 'Market Data',
      icon: '📊',
      provider: 'Nielsen / Euromonitor',
      coverage: t ? 'Nacional' : 'National',
      recency: '2023-Q4',
      fidelity: 0.92,
      description: t ? 'Participación de mercado, volumen, valor, precios' : 'Market share, volume, value, prices',
      is_proprietary: false,
    },
    {
      id: 'consumer',
      name: t ? 'Datos de Consumidor' : 'Consumer Data',
      icon: '👥',
      provider: 'Kantar / Internal Research',
      coverage: t ? 'Panel de 5,000+ hogares' : '5,000+ household panel',
      recency: '2023-Q3',
      fidelity: 0.85,
      description: t ? 'Segmentación, preferencias, comportamiento de compra' : 'Segmentation, preferences, purchase behavior',
      is_proprietary: true,
    },
    {
      id: 'channel',
      name: t ? 'Datos de Canal' : 'Channel Data',
      icon: '🏪',
      provider: 'Internal Sales / Distributor Reports',
      coverage: t ? 'Cobertura de 80% de canales' : '80% channel coverage',
      recency: '2023-Q4',
      fidelity: 0.88,
      description: t ? 'Distribución, precios por canal, promociones' : 'Distribution, channel prices, promotions',
      is_proprietary: true,
    },
    {
      id: 'competitor',
      name: t ? 'Inteligencia Competitiva' : 'Competitive Intelligence',
      icon: '⚔️',
      provider: 'Market Intelligence / Public Data',
      coverage: t ? 'Top 10 competidores' : 'Top 10 competitors',
      recency: '2023-Q4',
      fidelity: 0.78,
      description: t ? 'Estrategias, lanzamientos, pricing, movimientos' : 'Strategies, launches, pricing, moves',
      is_proprietary: false,
    },
    {
      id: 'financial',
      name: t ? 'Datos Financieros' : 'Financial Data',
      icon: '💰',
      provider: 'Client ERP / Internal Systems',
      coverage: t ? 'Completo' : 'Complete',
      recency: t ? 'Tiempo real' : 'Real-time',
      fidelity: 0.95,
      description: t ? 'Costos, márgenes, P&L por producto' : 'Costs, margins, P&L by product',
      is_proprietary: true,
    },
  ];

  // Calculate overall data quality
  const avgFidelity = dataSources.reduce((s, d) => s + d.fidelity, 0) / dataSources.length;
  const qualityTier = quality?.overallTier || (avgFidelity > 0.85 ? 'gold' : avgFidelity > 0.7 ? 'silver' : 'bronze');

  const tierColors = {
    gold: 'bg-warning-500/20 text-warning-400 border-warning-500/30',
    silver: 'bg-gray-400/20 text-text-secondary border-gray-400/30',
    bronze: 'bg-amber-700/20 text-amber-600 border-amber-700/30',
  };

  const fidelityColor = (f: number) => {
    if (f >= 0.9) return 'text-success-400';
    if (f >= 0.8) return 'text-primary-400';
    if (f >= 0.7) return 'text-warning-400';
    return 'text-danger-400';
  };

  return (
    <div className="space-y-6">
      {/* Overall Quality Score */}
      <div className="card p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-medium text-text-secondary uppercase tracking-wider">
            {t ? 'Calidad General de Datos' : 'Overall Data Quality'}
          </h3>
          <span className={`px-3 py-1 rounded-full text-sm font-medium border ${tierColors[qualityTier as keyof typeof tierColors]}`}>
            {qualityTier === 'gold' ? '🥇 Gold' : qualityTier === 'silver' ? '🥈 Silver' : '🥉 Bronze'}
          </span>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-4xl font-bold text-white">{(avgFidelity * 100).toFixed(0)}%</div>
          <div className="flex-1">
            <div className="h-3 bg-surface-600 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full ${avgFidelity >= 0.85 ? 'bg-success-500' : avgFidelity >= 0.7 ? 'bg-warning-500' : 'bg-danger-500'}`}
                style={{ width: `${avgFidelity * 100}%` }}
              />
            </div>
            <p className="text-xs text-text-tertiary mt-1">
              {t ? 'Promedio de fidelidad de todas las fuentes' : 'Average fidelity across all sources'}
            </p>
          </div>
        </div>
      </div>

      {/* Data Sources List */}
      <div className="card p-5">
        <h3 className="text-sm font-medium text-text-secondary uppercase tracking-wider mb-4">
          {t ? 'Fuentes de Datos' : 'Data Sources'}
        </h3>
        <div className="space-y-3">
          {dataSources.map((source) => (
            <div key={source.id} className="p-4 bg-surface-700/50 rounded-lg border border-surface-600">
              <div className="flex items-start gap-3">
                <span className="text-2xl">{source.icon}</span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-medium text-white">{source.name}</span>
                    <span className={`text-sm font-bold ${fidelityColor(source.fidelity)}`}>
                      {(source.fidelity * 100).toFixed(0)}% {t ? 'fidelidad' : 'fidelity'}
                    </span>
                  </div>
                  <p className="text-sm text-text-secondary mb-2">{source.description}</p>
                  <div className="flex flex-wrap gap-2 text-xs">
                    <span className="px-2 py-0.5 bg-surface-600 text-text-secondary rounded">
                      📡 {source.provider}
                    </span>
                    <span className="px-2 py-0.5 bg-surface-600 text-text-secondary rounded">
                      📍 {source.coverage}
                    </span>
                    <span className="px-2 py-0.5 bg-primary-500/20 text-primary-400 rounded">
                      🕐 {source.recency}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Data Recency Timeline */}
      <div className="card p-5">
        <h3 className="text-sm font-medium text-text-secondary uppercase tracking-wider mb-4">
          {t ? 'Actualización de Datos' : 'Data Freshness'}
        </h3>
        <div className="relative">
          <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-surface-600" />
          {[
            { date: t ? 'Tiempo real' : 'Real-time', items: [t ? 'Datos Financieros' : 'Financial Data'], color: 'bg-success-500' },
            { date: '2023-Q4', items: [t ? 'Mercado' : 'Market', t ? 'Canal' : 'Channel', t ? 'Competencia' : 'Competition'], color: 'bg-primary-500' },
            { date: '2023-Q3', items: [t ? 'Consumidor' : 'Consumer'], color: 'bg-warning-500' },
          ].map((period, i) => (
            <div key={i} className="relative flex items-start gap-4 pb-4">
              <div className={`w-3 h-3 rounded-full ${period.color} mt-1.5 z-10`} />
              <div>
                <div className="text-sm font-medium text-white">{period.date}</div>
                <div className="text-xs text-text-tertiary">{period.items.join(' • ')}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Insight */}
      <InsightCard
        text={avgFidelity >= 0.85
          ? (t ? 'Excelente calidad de datos. Las simulaciones tendrán alta confiabilidad.' : 'Excellent data quality. Simulations will have high reliability.')
          : avgFidelity >= 0.7
            ? (t ? 'Buena calidad de datos. Algunas áreas podrían mejorar con datos más recientes.' : 'Good data quality. Some areas could improve with more recent data.')
            : (t ? 'Calidad de datos mejorable. Considere actualizar fuentes para mejor precisión.' : 'Data quality could improve. Consider updating sources for better accuracy.')}
        type={avgFidelity >= 0.85 ? 'success' : avgFidelity >= 0.7 ? 'info' : 'warning'}
        sources={dataSources.filter(ds => ds.fidelity >= 0.8).slice(0, 3).map(ds => ({
          name: ds.name,
          type: ds.is_proprietary ? 'internal' as const : 'estimate' as const,
          confidence: ds.fidelity
        }))}
        language={language}
      />
    </div>
  );
}

export default UnderstandPage;
